import { Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { AppointmentService } from 'src/app/services/appointments.service';
import { Router, ActivatedRoute } from '@angular/router';
import { INutritionConsultation } from 'src/app/commons/interfaces/nutrition-consultation.interface';
import { PrimeraVezComponent } from 'src/app/pages/nutrition/medical-history-data/primera-vez/primera-vez.component';
import SeguimientoComponent from 'src/app/pages/nutrition/medical-history-data/seguimiento/seguimiento.component';
import { HospitalizacionComponent } from 'src/app/pages/nutrition/medical-history-data/hospitalizacion/hospitalizacion.component';
import { PatientService } from 'src/app/services/patient.service';
import {
  IPatient,
  AppointmentMeta,
  AppointmentVitalSigns,
  INotaMedica,
  IAppointment,
} from 'src/app/commons/interfaces';
import { getAge } from 'src/app/commons/utils';
import { StateService } from '@services/state.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IonChip, ToastController } from '@ionic/angular/standalone';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { NutritionConsultationService } from '@services/nutrition-consultation.service';
import { ExpedienteService } from '@services/expediente.service';
import { VitalSignsService } from '@services/vital-signs.service';
import { PatientTimelineService } from '@services/patient-timeline.service';
import { HistoricoComponent } from 'src/app/pages/expediente/expediente-digital/sections/historico/historico.component';
import {
  IHistorico,
  IParaclinico,
  labResultToParaclinico,
} from 'src/app/pages/expediente/expediente-digital/sections/paraclinicos/paraclinicos-page/interfaces/paraclinico.interface';
import { LabResultsService } from '@services/lab-results.service';
import { IndicacionesComponent } from 'src/app/pages/expediente/expediente-digital/sections/indicaciones/indicaciones.component';
import { NotasInternasModalComponent } from './notas-internas-modal/notas-internas-modal.component';
import { ResumenNotaComponent } from './resumen-nota/resumen-nota.component';
import { PatientInternalNotesService, NotaInterna } from '@services/patient-internal-notes.service';
import { PatientHistoricoPdfService, IHistoricoPdf } from '@services/patient-historico-pdf.service';
import { AuthService } from 'src/app/auth/auth.service';
import { PrescriptionService, IPrescriptionResponse } from '@services/prescription.service';
import { QuillModule } from 'ngx-quill';

// Interfaces para Indicaciones
interface IMedicamentoReceta {
  nombre: string;
  dosis: string;
  frecuencia: string;
  via: string;
  duracion: string;
  indicaciones: string;
}

interface IMedicamentoFormularioUI {
  nombre: string;
  detalles: string[];
  indicaciones?: string;
}

interface IMedicamentoRecetaCardUI {
  id: string;
  recetaId?: string | number;
  titulo: string;
  detalles: string[];
  indicaciones?: string;
  textoLibre?: string;
  fechaCreacion?: string;
}

interface IRecetaSolicitud {
  id: string;
  tipo: 'receta' | 'laboratorio' | 'gabinete' | 'interno';
  nombre: string;
  fecha_creacion: string;
  ultima_modificacion: string;
  finalizada: boolean;
  origen: string;
  modo_entrada?: 'formulario' | 'texto';
  contenido_html?: string;
  medicamentos?: IMedicamentoReceta[];
  estudios?: any[];
  notas?: string;
  backendId?: number;
}

interface IPlantillaReceta {
  id: string;
  nombre: string;
  descripcion?: string;
  medicamentos: IMedicamentoReceta[];
  fecha_creacion: string;
  ultima_modificacion: string;
}


interface SignoVital {
  nombre: string;
  abbr: string;
  valor: string;
  icono: string | string[]; // <-- Corregido: puede ser string o array
  color: string;
}

interface Medicamento {
  nombre: string;
  dosis: string;
  frecuencia: string;
  activo: boolean;
}

const PRIORIDAD_SIGNOS = ['Presión arterial', 'Frecuencia cardiaca', 'Temperatura', 'SpO₂'];

const ICONS_SIGNOS: Record<string, string | string[]> = {
  'Presión arterial':
    'M18.5869 5.25957C16.9524 3.3719 15.1127 1.67209 13.102 0.191572C12.924 0.0668851 12.712 0 12.4947 0C12.2773 0 12.0653 0.0668851 11.8873 0.191572C9.88029 1.67271 8.04426 3.37249 6.41307 5.25957C2.77549 9.43703 0.855469 13.8408 0.855469 17.9957C0.855469 21.084 2.0823 24.0459 4.26606 26.2296C6.44983 28.4134 9.41165 29.6402 12.5 29.6402C15.5883 29.6402 18.5501 28.4134 20.7339 26.2296C22.9176 24.0459 24.1444 21.084 24.1444 17.9957C24.1444 13.8408 22.2244 9.43703 18.5869 5.25957ZM19.8902 19.2316C19.6157 20.7649 18.8781 22.1772 17.7766 23.2785C16.675 24.3798 15.2625 25.1171 13.7292 25.3913C13.6728 25.4003 13.6157 25.4051 13.5585 25.4059C13.293 25.4058 13.0372 25.3059 12.8418 25.1261C12.6465 24.9462 12.5259 24.6995 12.5039 24.4349C12.4819 24.1703 12.5602 23.9071 12.7232 23.6974C12.8862 23.4878 13.122 23.3471 13.3839 23.3032C15.5765 22.9341 17.437 21.0736 17.8088 18.877C17.8558 18.6001 18.0109 18.3532 18.24 18.1907C18.469 18.0282 18.7532 17.9633 19.0301 18.0103C19.307 18.0573 19.5539 18.2124 19.7164 18.4415C19.879 18.6705 19.9439 18.9547 19.8969 19.2316H19.8902Z',
  'Frecuencia cardiaca':
    'M23.689 0C26.3819 0 28.6376 0.996393 30.4558 2.98918C32.2741 4.98196 33.1827 7.35176 33.1817 10.0986C33.1817 10.5833 33.1547 11.0616 33.1009 11.5334C33.047 12.0052 32.9528 12.4695 32.8181 12.9262H22.7195L19.9727 8.80596C19.8381 8.59052 19.6496 8.41548 19.4072 8.28083C19.1648 8.14618 18.909 8.07886 18.6397 8.07886C18.2896 8.07886 17.9735 8.18658 17.6912 8.40202C17.409 8.61745 17.2135 8.88675 17.1047 9.2099L14.9234 15.7538L13.5096 13.6533C13.375 13.4378 13.1865 13.2628 12.9441 13.1281C12.7017 12.9935 12.4459 12.9262 12.1766 12.9262H1.22976C1.09511 12.4684 1.00086 12.0041 0.946999 11.5334C0.89314 11.0627 0.866211 10.5978 0.866211 10.139C0.866211 7.36523 1.76835 4.98196 3.57263 2.98918C5.37691 0.996393 7.62552 0 10.3185 0C11.6111 0 12.8299 0.25583 13.975 0.767492C15.12 1.27915 16.1363 1.99279 17.0239 2.90839C17.8857 1.99279 18.8891 1.27915 20.0341 0.767492C21.1792 0.25583 22.3975 0 23.689 0ZM17.0239 29.0839C16.5392 29.0839 16.0749 28.9966 15.6311 28.8221C15.1873 28.6476 14.7899 28.3848 14.4387 28.0336L3.61302 17.1676C3.45145 17.006 3.30333 16.8444 3.16869 16.6828C3.03404 16.5213 2.89939 16.3462 2.76474 16.1577H11.2879L14.0348 20.2779C14.1694 20.4934 14.3579 20.6684 14.6003 20.8031C14.8426 20.9377 15.0985 21.005 15.3678 21.005C15.7179 21.005 16.041 20.8973 16.3372 20.6819C16.6335 20.4664 16.8354 20.1972 16.9431 19.874L19.1244 13.3301L20.4978 15.4306C20.6594 15.6461 20.8614 15.8211 21.1038 15.9558C21.3461 16.0904 21.602 16.1577 21.8713 16.1577H31.2427L30.8388 16.6425L30.4348 17.1272L19.5688 28.0336C19.2187 28.3837 18.8282 28.6466 18.3973 28.8221C17.9665 28.9977 17.5087 29.085 17.0239 29.0839Z',
  'Frecuencia respiratoria':
    'M14.4794 7.1118L14.7988 11.2724L15.2742 10.9567C15.5346 10.783 15.748 10.5478 15.8956 10.2718C16.0432 9.99578 16.1204 9.68763 16.1204 9.37465V1.90155C16.1204 1.39723 16.3207 0.913561 16.6774 0.556951C17.034 0.200341 17.5176 0 18.0219 0C18.5263 0 19.0099 0.200341 19.3665 0.556951C19.7232 0.913561 19.9235 1.39723 19.9235 1.90155V9.37465C19.9235 9.68763 20.0007 9.99578 20.1483 10.2718C20.2959 10.5478 20.5093 10.783 20.7697 10.9567L21.2451 11.2724L21.5645 7.1118C21.7946 4.11876 25.2383 2.55949 27.6381 4.36026L28.7505 5.19314C31.5348 7.28194 33.3901 10.3783 33.9189 13.8186L35.9726 27.1656C36.6533 31.5905 32.2037 35.0399 28.0869 33.2771L26.0807 32.4157C24.2564 31.6336 22.7226 30.3 21.6944 28.6021C20.6662 26.9042 20.1953 24.927 20.3475 22.9479L20.9104 15.6212L18.6609 14.119C18.4365 13.9695 18.223 13.8043 18.0219 13.6246C17.8209 13.8043 17.6074 13.9695 17.383 14.119L15.1335 15.6212L15.6964 22.9479C15.849 24.9273 15.3783 26.9049 14.3501 28.6032C13.3219 30.3015 11.7879 31.6354 9.96318 32.4176L7.95514 33.2771C3.84018 35.0399 -0.611346 31.5905 0.0694088 27.1656L2.12308 13.8186C2.65235 10.378 4.50839 7.28159 7.2934 5.19314L8.4039 4.36026C10.8056 2.55949 14.2493 4.11876 14.4794 7.1118Z',
  Temperatura:
    'M8.43134 2.70143C7.54504 2.70143 6.69503 3.12835 6.06832 3.88827C5.44161 4.6482 5.08953 5.67888 5.08953 6.75357V20.227L4.71859 20.6295C3.87773 21.5413 3.28502 22.7411 3.01889 24.0704C2.75275 25.3996 2.82575 26.7954 3.22821 28.0732C3.63068 29.351 4.34363 30.4505 5.27274 31.2262C6.20184 32.0019 7.3033 32.4173 8.43134 32.4173C9.55938 32.4173 10.6608 32.0019 11.5899 31.2262C12.519 30.4505 13.232 29.351 13.6345 28.0732C14.0369 26.7954 14.1099 25.3996 13.8438 24.0704C13.5777 22.7411 12.9849 21.5413 12.1441 20.6295L11.7731 20.227V6.75357C11.7731 5.67888 11.4211 4.6482 10.7943 3.88827C10.1676 3.12835 9.31764 2.70143 8.43134 2.70143ZM2.86166 6.75357C2.86166 4.96241 3.44847 3.24462 4.49298 1.97808C5.5375 0.711535 6.95417 0 8.43134 0C9.90851 0 11.3252 0.711535 12.3697 1.97808C13.4142 3.24462 14.001 4.96241 14.001 6.75357V19.0464C15.0756 20.3763 15.8025 22.0612 16.0905 23.89C16.3785 25.7188 16.2148 27.6099 15.62 29.3263C15.0252 31.0428 14.0257 32.5081 12.7469 33.5385C11.468 34.5689 9.96677 35.1185 8.43134 35.1185C6.8959 35.1185 5.39465 34.5689 4.11581 33.5385C2.83697 32.5081 1.83748 31.0428 1.24266 29.3263C0.64785 27.6099 0.484188 25.7188 0.772202 23.89C1.06022 22.0612 1.78708 20.3763 2.86166 19.0464V6.75357ZM7.3174 12.1564C7.3174 11.7982 7.43476 11.4546 7.64367 11.2013C7.85257 10.948 8.1359 10.8057 8.43134 10.8057C8.72677 10.8057 9.01011 10.948 9.21901 11.2013C9.42791 11.4546 9.54527 11.7982 9.54527 12.1564V21.8411C10.2888 22.1597 10.9155 22.7868 11.3146 23.6116C11.7137 24.4363 11.8595 25.4056 11.7261 26.3481C11.5928 27.2906 11.189 28.1456 10.5861 28.7619C9.98317 29.3783 9.21995 29.7164 8.43134 29.7164C7.64272 29.7164 6.8795 29.3783 6.27657 28.7619C5.67365 28.1456 5.26984 27.2906 5.13653 26.3481C5.00322 25.4056 5.14899 24.4363 5.54807 23.6116C5.94716 22.7868 6.57386 22.1597 7.3174 21.8411V12.1564Z',
  'SpO₂': [
    'M26.8886 30.66C24.3832 30.66 22.6575 29.5059 21.6128 27.9597C20.6295 26.5034 20.3186 24.8083 20.3186 23.652V20.148C20.3186 18.9917 20.6295 17.2966 21.6128 15.8403C22.6575 14.2941 24.3832 13.14 26.8886 13.14C29.3939 13.14 31.1196 14.2941 32.1643 15.8403C33.1476 17.2966 33.4585 18.9917 33.4585 20.148V23.652C33.4585 24.8083 33.1476 26.5034 32.1643 27.9597C31.1196 29.5059 29.3939 30.66 26.8886 30.66ZM23.6036 23.652C23.6036 24.2477 23.7853 25.1806 24.2803 25.9143C24.7139 26.5581 25.4519 27.156 26.8886 27.156C28.3252 27.156 29.0632 26.5581 29.4968 25.9143C29.9918 25.1806 30.1736 24.2477 30.1736 23.652V20.148C30.1736 19.5523 29.9918 18.6194 29.4968 17.8857C29.0632 17.2419 28.3252 16.644 26.8886 16.644C25.4519 16.644 24.7139 17.2419 24.2803 17.8857C23.8518 18.5637 23.6178 19.3462 23.6036 20.148V23.652Z',
    'M38.3859 25.7325C38.3917 25.5401 38.3423 25.3502 38.2435 25.185H37.4333C37.3873 25.259 37.3526 25.3395 37.3303 25.4237C37.2759 25.6331 37.1806 25.8296 37.05 26.002C36.9194 26.1744 36.756 26.3194 36.5692 26.4285C36.3825 26.5376 36.176 26.6087 35.9616 26.6378C35.7473 26.6669 35.5293 26.6534 35.3202 26.598C35.1111 26.5426 34.915 26.4465 34.7432 26.3151C34.5713 26.1838 34.4271 26.0198 34.3188 25.8325C34.2106 25.6453 34.1403 25.4385 34.1122 25.224C34.084 25.0095 34.0985 24.7916 34.1548 24.5828C34.4877 23.3213 35.552 21.9 37.2909 21.9H38.3859C39.4721 21.9 40.3306 22.4913 40.8606 23.1987C41.3906 23.906 41.6709 24.8061 41.6709 25.7325C41.6709 26.7092 41.3577 27.6663 40.762 28.3934L39.0035 30.66H40.0284C40.464 30.66 40.8818 30.833 41.1898 31.1411C41.4979 31.4491 41.6709 31.8669 41.6709 32.3025C41.6709 32.7381 41.4979 33.1559 41.1898 33.4639C40.8818 33.772 40.464 33.945 40.0284 33.945H35.6484C35.3409 33.9453 35.0395 33.8592 34.7785 33.6966C34.5175 33.534 34.3074 33.3014 34.1721 33.0253C34.0368 32.7492 33.9817 32.4407 34.0131 32.1348C34.0445 31.8289 34.1611 31.538 34.3497 31.2951L38.1822 26.3588L38.2216 26.3107C38.3381 26.1411 38.3958 25.938 38.3859 25.7325ZM11.5584 4.38H9.3684V9.1323C11.9197 10.039 13.7484 12.4721 13.7484 15.33H0.608398C0.608398 12.4699 2.43705 10.0368 4.9884 9.1323V4.38H2.7984V0H11.5584V4.38ZM13.7484 41.5005V19.71H0.608398V41.5005C0.608398 42.7707 1.6377 43.8 2.9079 43.8H11.4489C12.7191 43.8 13.7484 42.7707 13.7484 41.5005Z',
  ],
  Peso: 'M40.2099 38.2219C40.0921 40.3416 38.339 42 36.2161 42L11.784 42C9.66102 42 7.90787 40.3415 7.79011 38.2219L6.23456 10.2219C6.10718 7.92907 7.93206 6 10.2284 6L37.7716 6C40.068 6 41.8928 7.92908 41.7655 10.2219L40.2099 38.2219ZM34.2442 17.7786C35.064 17.2385 35.2702 16.1677 34.5974 15.471C33.4729 14.3066 32.139 13.3305 30.6524 12.5895C28.6303 11.5817 26.3828 11.0377 24.0939 11.0019C21.805 10.9661 19.5395 11.4396 17.4828 12.3837C15.9707 13.0778 14.603 14.0117 13.4379 15.1403C12.7407 15.8156 12.9089 16.8922 13.7092 17.4577L18.0211 20.5042C18.8214 21.0696 19.9487 20.8868 20.7779 20.3594C20.9902 20.2244 21.2129 20.1032 21.4445 19.9969C22.1757 19.6612 22.9766 19.4829 23.7891 19.4741L27.1821 15.5729C27.5445 15.1562 28.1761 15.1122 28.5929 15.4746C29.0096 15.837 29.0536 16.4687 28.6912 16.8854L26.0608 19.9098C26.1835 19.9605 26.3044 20.0155 26.4232 20.0747C26.6509 20.1882 26.8692 20.3163 27.0766 20.4579C27.8867 21.0108 29.0068 21.2288 29.8267 20.6887L34.2442 17.7786Z',
  Talla: [
    'M0.00156614 1.8598L0.376383 8.78403C0.435564 10.0466 0.928743 10.7765 1.81647 11.4275L6.09725 14.5641C6.62989 14.9389 6.9258 15.2151 6.9258 16.1423V41.8861C6.9258 42.9515 7.7938 43.7998 8.85906 43.7998C9.92432 43.7998 10.7726 42.9515 10.7726 41.8861V28.0378C10.7726 27.6826 11.0882 27.3669 11.4433 27.3669C11.7984 27.3669 12.0943 27.6826 12.0943 28.0378V41.8861C12.0943 42.9515 12.9623 43.7998 14.0276 43.7998C15.0928 43.7998 15.9411 42.9515 15.9411 41.8861V19.0224C15.9411 18.6674 16.237 18.3911 16.5921 18.3911C16.9471 18.3911 17.2234 18.6674 17.2234 19.0224V26.6173C17.2234 27.6038 18.0124 28.3731 18.9791 28.3731C19.9457 28.3731 20.7347 27.6038 20.7347 26.6173V18.1348C20.7347 13.775 17.539 10.8751 12.8834 10.8751H8.78014C7.59652 10.8751 6.86662 10.6384 5.91972 9.96765L3.8681 8.50786L3.49327 1.68225C3.45382 0.715625 2.62527 -0.0734619 1.65865 0.00544683C0.67229 0.0843554 -0.0378881 0.893168 0.00156614 1.8598ZM11.4433 8.66567C13.8303 8.66567 15.7438 6.75214 15.7438 4.36515C15.7438 1.97816 13.8303 0.0646283 11.4433 0.0646283C9.05634 0.0646283 7.1428 1.97816 7.1428 4.36515C7.1428 6.75214 9.05634 8.66567 11.4433 8.66567Z',
    'M27.8881 43.3496L31.2063 38.8808C31.9067 37.947 31.4898 36.8465 30.3393 36.8465L28.2883 36.8465V32.5445C28.2883 31.8108 27.7213 31.2272 26.9877 31.2272C26.2707 31.2272 25.6871 31.8108 25.6871 32.5445L25.6871 36.8465H23.6694C22.4689 36.8465 22.1187 37.9804 22.7857 38.8808L26.1206 43.3496C26.5875 43.9499 27.4379 43.9499 27.8881 43.3496ZM26.9877 12.5684C27.7213 12.5684 28.2883 12.0014 28.2883 11.2677V6.96577L30.3393 6.96577C31.4898 6.96577 31.9067 5.84852 31.2063 4.93146L27.8881 0.462725C27.4379 -0.154242 26.5875 -0.154242 26.1206 0.462725L22.7857 4.93146C22.1187 5.81522 22.4689 6.96577 23.6694 6.96577H25.6871V11.2677C25.6871 12.0014 26.2707 12.5684 26.9877 12.5684Z',
    'M27.2184 27.2065H26.7223C25.5934 27.2065 24.8121 26.4995 24.8121 25.2378C24.8121 24.769 24.9293 24.3237 25.1481 24.0073C25.316 23.7612 25.5543 23.5815 25.8082 23.5815C26.0856 23.5815 26.3004 23.7768 26.3004 24.0698C26.3004 24.2807 26.1871 24.4057 26.0856 24.5268C25.9176 24.6909 25.8121 24.8862 25.8121 25.1557C25.8121 25.6675 26.2028 25.9135 26.7887 25.9135H27.1559C27.7731 25.9135 28.1871 25.7182 28.1871 25.144C28.1871 24.8706 28.0778 24.6635 27.8981 24.48C27.8043 24.3628 27.6949 24.2456 27.6949 24.0385C27.6949 23.7534 27.8824 23.5698 28.1715 23.5698C28.4332 23.5698 28.6989 23.7612 28.8864 24.062C29.0817 24.3745 29.1871 24.7846 29.1871 25.2378C29.1871 26.4565 28.4801 27.2065 27.2184 27.2065Z',
    'M28.4371 16.5932C28.8864 16.5932 29.1246 16.8198 29.1246 17.2221C29.1246 17.6245 28.8864 17.8471 28.4371 17.8471H26.5856C26.1246 17.8471 25.8629 18.0737 25.8629 18.4565C25.8629 18.8432 26.1559 19.0893 26.6207 19.0893H28.4371C28.8864 19.0893 29.1246 19.2964 29.1246 19.687C29.1246 20.0815 28.8864 20.2885 28.4371 20.2885H26.5934C26.1364 20.2885 25.8629 20.5229 25.8629 20.9057C25.8629 21.2885 26.152 21.5307 26.6207 21.5307H28.4371C28.8864 21.5307 29.1246 21.7534 29.1246 22.1557C29.1246 22.5581 28.8864 22.7846 28.4371 22.7846H25.5465C25.1324 22.7846 24.8785 22.5503 24.8785 22.1714C24.8785 21.812 25.1129 21.5854 25.5074 21.5659H25.6246V21.5034C25.1559 21.3745 24.8512 20.9253 24.8512 20.3589C24.8512 19.769 25.1481 19.351 25.6403 19.2182V19.1596C25.1637 18.9995 24.8512 18.5229 24.8512 17.9175C24.8512 17.0971 25.359 16.5932 26.191 16.5932H28.4371Z',
  ],
  IMC: 'M40.2099 38.2219C40.0921 40.3416 38.339 42 36.2161 42L11.784 42C9.66102 42 7.90787 40.3415 7.79011 38.2219L6.23456 10.2219C6.10718 7.92907 7.93206 6 10.2284 6L37.7716 6C40.068 6 41.8928 7.92908 41.7655 10.2219L40.2099 38.2219ZM34.2442 17.7786C35.064 17.2385 35.2702 16.1677 34.5974 15.471C33.4729 14.3066 32.139 13.3305 30.6524 12.5895C28.6303 11.5817 26.3828 11.0377 24.0939 11.0019C21.805 10.9661 19.5395 11.4396 17.4828 12.3837C15.9707 13.0778 14.603 14.0117 13.4379 15.1403C12.7407 15.8156 12.9089 16.8922 13.7092 17.4577L18.0211 20.5042C18.8214 21.0696 19.9487 20.8868 20.7779 20.3594C20.9902 20.2244 21.2129 20.1032 21.4445 19.9969C22.1757 19.6612 22.9766 19.4829 23.7891 19.4741L27.1821 15.5729C27.5445 15.1562 28.1761 15.1122 28.5929 15.4746C29.0096 15.837 29.0536 16.4687 28.6912 16.8854L26.0608 19.9098C26.1835 19.9605 26.3044 20.0155 26.4232 20.0747C26.6509 20.1882 26.8692 20.3163 27.0766 20.4579C27.8867 21.0108 29.0068 21.2288 29.8267 20.6887L34.2442 17.7786Z',
};

@Component({
  selector: 'app-perfil-paciente',
  standalone: true,
  imports: [
    IonChip,
    CommonModule,
    FormsModule,
    PrimeraVezComponent,
    SeguimientoComponent,
    HospitalizacionComponent,
    HistoricoComponent,
    IndicacionesComponent,
    NotasInternasModalComponent,
    ResumenNotaComponent,
    QuillModule,
  ],
  templateUrl: './perfil-paciente.component.html',
  styleUrls: ['./perfil-paciente.component.scss'],
  animations: [
    trigger('fadeSlide', [
      state('perfil', style({ opacity: 1, transform: 'translateX(0)' })),
      state('resumen', style({ opacity: 1, transform: 'translateX(0)' })),
      transition('perfil => resumen', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('300ms ease-out'),
      ]),
      transition('resumen => perfil', [
        style({ opacity: 0, transform: 'translateX(-20px)' }),
        animate('300ms ease-out'),
      ]),
    ]),
  ],
})
export class PerfilPacienteComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('historicoSeccion') historicoSeccion?: ElementRef<HTMLElement>;
  @ViewChildren('notaCardResumenCompleto') notaCardsResumenCompleto?: QueryList<ElementRef<HTMLElement>>;
  medicamentosActualesPlain: string = '';
  isLoadingMedicamentos: boolean = false;
  isLoadingDiagnosticos: boolean = false;
  isLoadingHistorico: boolean = false;
  diagnosticosActuales: any[] = [];

  // Mostrar hora y doctor de la cita actual
  appointmentTimeDisplay: string = '—';
  appointmentDoctorName: string = '—';

  // Indicaciones médicas
  indicacionesMedicas: IRecetaSolicitud[] = [];
  plantillasRecetas: IPlantillaReceta[] = [];
  ultimaReceta: IRecetaSolicitud | null = null;
  medicamentosCompactos: string[] = [];
  medicamentosRecetasUI: IMedicamentoRecetaCardUI[] = [];
  recetasExpandidas: Set<string> = new Set<string>();
  modalSelectedRecetaId: string | number | null = null;
  modalIndicacionesAbierto: boolean = false;
  modalResumenCompletoAbierto: boolean = false;
  notasResumenCompleto: any[] = [];
  isLoadingResumenCompleto: boolean = false;
  errorResumenCompleto: string | null = null;
  indiceNotaActualResumenCompleto: number = -1;

  // Modal Ver Paraclínico (desde histórico)
  modalVerParaclinicoAbierto: boolean = false;
  paraclinicoParaVer: (IParaclinico & { pdfUrlSafe?: SafeResourceUrl }) | null = null;
  modalHistoricoPdfAbierto: boolean = false;
  historicoPdfVista: 'lista' | 'visor' = 'lista';
  historicoPdfSeleccionado: (IHistoricoPdf & { file_url_safe?: SafeResourceUrl }) | null = null;
  isLoadingHistoricoPdfs: boolean = false;
  subiendoHistoricoPdf: boolean = false;
  errorHistoricoPdf: string | null = null;
  archivoHistoricoPdf: File | null = null;
  historicoPdfs: IHistoricoPdf[] = [];

  getAge = getAge;
  @Input() patient!: IPatient;
  @Input() appointmentId: number | null = null;
  @Input() citaSeleccionada: INutritionConsultation | null = null;
  @Input() patientId!: string;

  // Notas médicas ligadas a la cita (appointment.meta.notas_medicas)
  notasMedicas: INotaMedica[] = [];
  modalNotaAbierta = false;
  notaForm: Partial<INotaMedica> = { titulo: '', nota: '' };
  editingNotaId: string | null = null;

  // campo que muestra la "nota interna" en la UI principal (se sincroniza con la nota más reciente)
  notaInterna: string = '';

  // Histórico del paciente
  historico: IHistorico[] = [];
  paraclinicosDisponibles: IParaclinico[] = [];
  mostrarHistorico = false;
  signosVitales: any[] = [];
  headerCount = 4;

  diagnosticos: any[] = [];

  medicamentos: Medicamento[] = [];
  svOpen = false;
  mostrarHeaderSignos = false;

  currentPatient: IPatient | null = null;
  patientPhotoUrl: string = 'assets/avatar.svg';
  isLoadingPatient = false;
  private subscription: Subscription = new Subscription();
  private destroy$ = new Subject<void>();
  private isReloadingPerfilCoreSections = false;
  private lastPerfilCoreReloadAt = 0;
  private readonly PERFIL_CORE_RELOAD_COOLDOWN_MS = 1200;
  private readonly RECETA_COLLAPSE_MIN_CHARS = 220;

  // === NOTAS INTERNAS DEL PACIENTE (scope: paciente, no cita) ===
  showNotasModal = false;
  notasInternas: NotaInterna[] = [];
  notasNuevasCount: number = 0;

  // === TAB TOGGLE: PERFIL vs RESUMEN DE NOTA ===
  vistaActual: 'perfil' | 'resumen' = 'perfil';
  notaSeleccionada: any = null;
  isLoadingNota: boolean = false;
  errorCargaNota: string | null = null;

  // Recetas históricas del paciente (desde backend)
  recetasHistoricas: IPrescriptionResponse[] = [];

  constructor(
    private apptSvc: AppointmentService,
    private router: Router,
    private route: ActivatedRoute,
    private stateService: StateService,
    private patientService: PatientService,
    private nutritionConsultationService: NutritionConsultationService,
    private expedienteService: ExpedienteService,
    private vitalSignsService: VitalSignsService,
    private patientTimelineService: PatientTimelineService,
    private labResultsService: LabResultsService,
    private patientInternalNotesService: PatientInternalNotesService,
    private patientHistoricoPdfService: PatientHistoricoPdfService,
    private authService: AuthService,
    private prescriptionService: PrescriptionService,
    private sanitizer: DomSanitizer,
    private toastController: ToastController,
  ) {}

  /**
   * Muestra un toast con mensaje y color
   */
  private async showToast(
    message: string,
    color: 'success' | 'danger' | 'warning' = 'success'
  ): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color,
      position: 'bottom',
    });
    await toast.present();
  }

  private getCurrentPatientIdAsNumber(): number | null {
    const rawPatientId = this.patientId || this.currentPatient?.id;
    if (!rawPatientId) return null;
    const numericPatientId = Number(rawPatientId);
    return Number.isFinite(numericPatientId) && numericPatientId > 0 ? numericPatientId : null;
  }

  private async reloadPerfilCoreSections(reason: string, force = false): Promise<void> {
    const patientId = this.getCurrentPatientIdAsNumber();
    if (!patientId) return;

    const now = Date.now();
    if (!force) {
      if (this.isReloadingPerfilCoreSections) return;
      if (now - this.lastPerfilCoreReloadAt < this.PERFIL_CORE_RELOAD_COOLDOWN_MS) return;
    }

    this.isReloadingPerfilCoreSections = true;
    this.lastPerfilCoreReloadAt = now;
    console.log(`[PerfilPaciente] Recargando secciones core (${reason}) para patientId:`, patientId);

    try {
      await Promise.all([
        this.loadAllDiagnosticosPaciente(patientId),
        this.loadMedicamentosDesdeRecetas(patientId),
        this.loadHistorico(),
      ]);
      await this.loadRecetasHistoricas();
    } catch (error) {
      console.error('[PerfilPaciente] Error recargando secciones core:', error);
    } finally {
      this.isReloadingPerfilCoreSections = false;
    }
  }

  private buildRecetaCardId(item: any, index: number, tipo: 'texto' | 'formulario'): string {
    const recetaId = item?.receta_id || item?.backendId || item?.id || 'sin-id';
    return `${tipo}-${recetaId}-${index}`;
  }

  isRecetaExpandida(cardId: string): boolean {
    return this.recetasExpandidas.has(cardId);
  }

  toggleRecetaExpandida(cardId: string): void {
    if (this.recetasExpandidas.has(cardId)) {
      this.recetasExpandidas.delete(cardId);
    } else {
      this.recetasExpandidas.add(cardId);
    }
  }

  shouldShowRecetaCollapse(text?: string): boolean {
    if (!text) return false;
    const normalized = text.trim();
    if (!normalized) return false;
    const lineCount = normalized.split('\n').length;
    return normalized.length > this.RECETA_COLLAPSE_MIN_CHARS || lineCount > 4;
  }

  getRecetaFechaDisplay(fechaIso?: string): string {
    if (!fechaIso) return '';
    const fecha = new Date(fechaIso);
    if (isNaN(fecha.getTime())) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
    const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));

    const hh = String(fecha.getHours()).padStart(2, '0');
    const mm = String(fecha.getMinutes()).padStart(2, '0');

    if (diffDays === 0) return `Hoy ${hh}:${mm}`;
    if (diffDays === 1) return `Ayer ${hh}:${mm}`;
    if (diffDays > 1 && diffDays < 7) return `Hace ${diffDays} dias`;
    return `${fecha.toLocaleDateString('es-MX')} ${hh}:${mm}`;
  }

  abrirRecetaCompletaDesdeTarjeta(recetaId?: string | number): void {
    this.abrirModalIndicaciones(recetaId);
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('🔵 PERFIL-PACIENTE ngOnChanges disparado:', changes);
    console.log('🔵 appointmentId anterior:', changes['appointmentId']?.previousValue);
    console.log('🔵 appointmentId nuevo:', changes['appointmentId']?.currentValue);
    console.log('🔵 patientId anterior:', changes['patientId']?.previousValue);
    console.log('🔵 patientId nuevo:', changes['patientId']?.currentValue);

    // Si cambia patientId (para expediente-completo sin appointmentId)
    if (changes['patientId'] && changes['patientId'].currentValue) {
      const patientId = changes['patientId'].currentValue;
      console.log('[PerfilPaciente] Cargando datos para patientId:', patientId, 'firstChange:', changes['patientId'].firstChange);
      this.loadPatientById(patientId);
      this.loadUltimosSignosVitalesDelExpediente(patientId);
      // Cargar medicamentos actuales desde la ultima nota del paciente
      this.reloadPerfilCoreSections('ngOnChanges patientId', true);

      // Cargar indicaciones medicas
      this.loadIndicaciones();
    }

    if (changes['appointmentId'] && changes['appointmentId'].currentValue) {
      console.log('[PerfilPaciente] Cargando datos para appointmentId:', changes['appointmentId'].currentValue);
      this.loadHistoricoByAppointmentId(changes['appointmentId'].currentValue);
      this.loadSignosVitalesDeCita(changes['appointmentId'].currentValue);
      // cargar notas de la cita cuando cambia appointmentId
      this.loadNotasFromAppointment();
      this.loadMedicamentosActualesFromAppointment();
      this.loadAppointmentSummary();
      // Cargar historico del paciente
      this.loadHistorico().then(() => {
        // Cargar recetas historicas despues de cargar el historico
        this.loadRecetasHistoricas();
      });
      this.reloadPerfilCoreSections('ngOnChanges appointmentId');
      // Cargar indicaciones medicas
      this.loadIndicaciones();
    } else {
      console.log('[PerfilPaciente] appointmentId no valido, no se cargan datos');
    }
  }

  async ngOnInit() {
    // ✅ SIEMPRE inicializar signos vitales con placeholders (incluso sin appointmentId)
    this.signosVitales = this.mapSignosVitales({});
    console.log('✅ Signos vitales inicializados con placeholders:', this.signosVitales);

    this.route.queryParams.subscribe(async (params) => {
      const patientId = params['patientId'];
      console.log('[PerfilPaciente] ngOnInit - patientId (query param):', patientId);
      if (patientId) {
        this.patientId = patientId;
        this.loadPatientById(patientId);
        // Cargar ultimos signos vitales del expediente
        this.loadUltimosSignosVitalesDelExpediente(patientId);
        this.reloadPerfilCoreSections('ngOnInit queryParams patientId', true);
      }
    });

    this.subscription.add(
      this.stateService.currentPatient$.subscribe((patient) => {
        this.currentPatient = patient;
        this.updatePatientPhotoUrl(); // ⚡ Actualizar URL solo cuando cambia el paciente
        const patientId = this.getCurrentPatientIdAsNumber();
        if (patientId) {
          this.reloadPerfilCoreSections('currentPatient state update');
        }
        console.log('currentPatient desde StateService:', this.currentPatient);
      }),
    );

    // ✅ Suscribirse a cambios de signos vitales desde StateService
    this.subscription.add(
      this.stateService.currentVitalSigns$.subscribe((vitalSigns) => {
        if (vitalSigns) {
          console.log('[PerfilPaciente] 🔄 Signos vitales actualizados desde StateService:', vitalSigns);
          this.signosVitales = this.mapSignosVitales(vitalSigns);
          console.log('[PerfilPaciente] ✅ Sidebar actualizado con nuevos valores');
        }
      }),
    );

    // Suscribirse a notificación de recetas guardadas en BD para recargar medicamentos
    this.subscription.add(
      this.stateService.recetasGuardadas$.subscribe((guardado) => {
        if (guardado && this.patientId) {
          console.log('[PerfilPaciente] Recetas guardadas en BD, recargando medicamentos...');
          this.loadMedicamentosDesdeRecetas(Number(this.patientId));
        }
      }),
    );

    // Suscribirse a cambios de diagnosticos en tiempo real
    // Cuando se agrega un diagnóstico en la nota actual, lo combinamos con los existentes
    this.subscription.add(
      this.stateService.currentDiagnosticos$.subscribe((diagnosticosNuevos) => {
        if (diagnosticosNuevos && diagnosticosNuevos.length > 0) {
          // Combinar diagnósticos nuevos con los existentes, evitando duplicados por código
          const codigosExistentes = new Set(
            this.diagnosticosActuales.map(dx => dx.code || dx.codigo || dx.cie10)
          );

          const diagnosticosUnicos = diagnosticosNuevos.filter(dx => {
            const codigo = dx.code || dx.codigo || dx.cie10;
            return codigo && !codigosExistentes.has(codigo);
          });

          if (diagnosticosUnicos.length > 0) {
            this.diagnosticosActuales = [...diagnosticosUnicos, ...this.diagnosticosActuales];
            console.log('[PerfilPaciente] Diagnósticos combinados, total:', this.diagnosticosActuales.length);
          }
        } else if (diagnosticosNuevos && diagnosticosNuevos.length === 0) {
          const hadDiagnosticos = this.diagnosticosActuales.length > 0;
          // Limpiar cuando se cambia de paciente o recuperar ante limpieza inesperada
          console.log('[PerfilPaciente] Diagnosticos limpiados (cambio de paciente)');
          this.diagnosticosActuales = [];
          if (hadDiagnosticos) {
            this.reloadPerfilCoreSections('diagnosticos limpiados desde state', true);
          }
        }
      }),
    );

    if (this.appointmentId) {
      await this.loadSignosVitalesDeCita(this.appointmentId);
      await this.loadMedicamentosActualesFromAppointment();
      await this.loadAppointmentSummary();
      await this.loadIndicaciones();
    }
  }
  mapSignosVitales(vitales: AppointmentVitalSigns): any[] {
    const signos: any[] = [
      {
        nombre: 'Presión arterial',
        valor:
          vitales.ta_sistolica != null && vitales.ta_diastolica != null
            ? `${vitales.ta_sistolica}/${vitales.ta_diastolica}`
            : '—',
        unidad: 'mmHg',
        icono: ICONS_SIGNOS['Presión arterial'],
      },
      {
        nombre: 'Frecuencia cardiaca',
        valor: vitales.fc ?? '—',
        unidad: 'lpm',
        icono: ICONS_SIGNOS['Frecuencia cardiaca'],
      },
      {
        nombre: 'Temperatura',
        valor: vitales.temp ?? '—',
        unidad: '°C',
        icono: ICONS_SIGNOS['Temperatura'],
      },
      {
        nombre: 'SpO₂',
        valor: vitales.spo2 ?? '—',
        unidad: '%',
        icono: ICONS_SIGNOS['SpO₂'],
      },
      {
        nombre: 'Peso',
        valor: vitales.peso ?? '—',
        unidad: 'kg',
        icono: ICONS_SIGNOS['Peso'],
      },
      {
        nombre: 'Talla',
        valor: vitales.talla_cm ?? '—',
        unidad: 'cm',
        icono: ICONS_SIGNOS['Talla'],
      },
      {
        nombre: 'IMC',
        valor: vitales.imc ?? '—',
        unidad: '',
        icono: ICONS_SIGNOS['IMC'],
      },
    ];
    // Puedes filtrar aquí si solo quieres mostrar algunos en la UI principal
    return signos;
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.unsubscribe();
  }
  async loadSignosVitalesDeCita(appointmentId: number) {
    console.log('🔵 loadSignosVitalesDeCita INICIO - appointmentId:', appointmentId);
    try {
      const appointment: IAppointment | null = await this.apptSvc.getById(appointmentId);
      console.log('🔵 Appointment cargado:', appointment);
      console.log('🔵 appointment.meta:', appointment?.meta);

      const vitales: AppointmentVitalSigns | undefined = (appointment?.meta as AppointmentMeta)?.signos_vitales;
      console.log('🔵 Signos vitales extraídos:', vitales);

      // ✅ SIEMPRE mapear signos vitales (con valores "—" si no existen)
      this.signosVitales = this.mapSignosVitales(vitales || {});
      console.log('✅ signosVitales mapeados:', this.signosVitales);
    } catch (e) {
      console.error('❌ Error cargando signos vitales:', e);
      this.signosVitales = [];
    }
  }
  // Formatea la fecha del diagnostico para mostrar en el sidebar
  formatFechaDiagnostico(fecha: string): string {
    if (!fecha) return '';
    try {
      const date = new Date(fecha);
      const dia = date.getDate().toString().padStart(2, '0');
      const mes = (date.getMonth() + 1).toString().padStart(2, '0');
      const anio = date.getFullYear();
      return `${dia}/${mes}/${anio}`;
    } catch {
      return '';
    }
  }

  // Getters para exponer los datos del paciente
  get fotoPerfil(): string {
    return this.patient?.photo ? this.patient.photo : 'assets/avatar.svg';
  }

  /**
   * ⚡ Método privado para actualizar la URL de la foto del paciente
   * Se ejecuta solo cuando currentPatient cambia, evitando re-evaluaciones en cada ciclo de detección
   */
  private updatePatientPhotoUrl(): void {
    if (!this.currentPatient) {
      this.patientPhotoUrl = 'assets/avatar.svg';
      return;
    }

    const photoPath = this.currentPatient.photo_url || this.currentPatient.photo;
    if (!photoPath) {
      this.patientPhotoUrl = 'assets/avatar.svg';
      return;
    }

    // Si la ruta ya incluye http, retornarla tal cual
    if (photoPath.includes('http')) {
      this.patientPhotoUrl = photoPath;
    } else {
      // Construir URL completa con el backend
      this.patientPhotoUrl = `http://medapp-core.test/storage/${photoPath}`;
    }
  }

  /**
   * @deprecated Usar patientPhotoUrl directamente en el template
   * Método mantenido temporalmente para compatibilidad
   */
  getPatientPhoto(): string {
    return this.patientPhotoUrl;
  }

  get nombreCompleto(): string {
    // Formato normalizado (prioridad)
    if (this.patient?.full_name) {
      return this.patient.full_name;
    }

    // Construir desde campos normalizados
    if (this.patient?.name || this.patient?.last_name) {
      return [this.patient.name, this.patient.last_name, this.patient.second_last_name]
        .filter(Boolean)
        .join(' ');
    }

    return this.patient?.full_name || '';
  }

  get sexo(): string {
    return this.patient?.gender || '-';
  }

  get Array() {
    return Array;
  }

  get edad(): number | string {
    // Formato normalizado (prioridad) - si viene computed del backend
    if (this.patient?.age !== undefined && this.patient?.age !== null) {
      return this.patient.age;
    }

    const fecha = this.patient?.birth_date;
    if (!fecha) return '-';
    const nacimiento = new Date(fecha);
    const hoy = new Date();
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  }

  // STOPPER para estatura
  get estatura(): string {
    return '-'; // No existe en la interfaz actual
  }
  get pesoSignosVitales(): string {
    const peso = this.signosVitales.find((s) => s.nombre === 'Peso');
    return peso && peso.valor !== '—' ? `${peso.valor} kg` : '-';
  }
  get tallaSignosVitales(): string {
    const talla = this.signosVitales.find((s) => s.nombre === 'Talla');
    return talla && talla.valor !== '—' ? `${talla.valor} cm` : '-';
  }
  // // STOPPER para tipo de atención
  // get tipoAtencion(): string {
  //   return this.patient?.datos_generales?.tipoAtencion || '-';
  // }

  // Métodos para cargar histórico y signos vitales
  async loadHistoricoByAppointmentId(appointmentId: number) {
    try {
      const appointment: IAppointment | null = await this.apptSvc.getById(appointmentId);
      const historicoCompleto = [...(appointment?.meta?.historico ?? [])];
      // Filtrar las recetas del histórico - las recetas se muestran en "Medicamentos actuales"
      this.historico = historicoCompleto.filter(
        (categoria: IHistorico) => categoria.titulo?.toLowerCase() !== 'recetas'
      );
    } catch (e) {
      this.historico = [];
    }
  }

  async loadPatientById(patientId: number | string): Promise<void> {
    if (this.isLoadingPatient) return;
    this.isLoadingPatient = true;
    try {
      const patient = await this.patientService.getPatient(Number(patientId));
      if (patient && patient.data) {
        this.currentPatient = patient.data;
        this.stateService.setCurrentPatient(patient.data); // Opcional: sincroniza el state global
        console.log('Paciente cargado en perfil-paciente:', patient.data);
        // Cargar notas internas del paciente
        await this.loadNotasInternas();
      } else {
        this.currentPatient = null;
        this.stateService.setCurrentPatient(null);
      }
    } catch (error) {
      this.currentPatient = null;
      this.stateService.setCurrentPatient(null);
      console.error('Error al cargar el paciente:', error);
    } finally {
      this.isLoadingPatient = false;
    }
  }

  async loadUltimosSignosVitalesDelExpediente(patientId: number): Promise<void> {
    try {
      const vitalSign = await this.vitalSignsService.getLatest(Number(patientId))
        .pipe(takeUntil(this.destroy$))
        .toPromise();
      if (vitalSign) {
        // Mapear del formato backend (VitalSign) al formato frontend (signosVitales)
        const signosVitalesMapeados = {
          ta_sistolica: vitalSign.blood_pressure_systolic,
          ta_diastolica: vitalSign.blood_pressure_diastolic,
          fc: vitalSign.heart_rate,
          fr: vitalSign.respiratory_rate,
          temp: vitalSign.temperature,
          spo2: vitalSign.oxygen_saturation, // ✅ Campo dedicado para SpO2
          peso: vitalSign.weight,
          talla_cm: vitalSign.height,
          imc: vitalSign.imc,
        };

        console.log('[PerfilPaciente] ✅ Signos vitales cargados de la BD (tabla vital_signs):', signosVitalesMapeados);

        // Actualizar el estado local del componente
        this.signosVitales = this.mapSignosVitales(signosVitalesMapeados);

        // Actualizar el estado global para que otros componentes se sincronicen
        this.stateService.setCurrentVitalSigns(signosVitalesMapeados);
      } else {
        console.log('[PerfilPaciente] ℹ️ No hay signos vitales previos en la BD (tabla vital_signs)');
        // Resetear signos vitales cuando no hay datos para este paciente
        this.signosVitales = this.mapSignosVitales({});
        this.stateService.setCurrentVitalSigns({});
      }
    } catch (error) {
      console.error('[PerfilPaciente] ❌ Error cargando signos vitales de la BD:', error);
      // Resetear signos vitales en caso de error
      this.signosVitales = this.mapSignosVitales({});
    }
  }

  editarNota() {
    // abrir modal para crear o editar la nota "interna" principal:
    if (this.notasMedicas && this.notasMedicas.length) {
      const sorted = [...this.notasMedicas].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      const reciente = sorted[0];
      this.abrirModalEditarNota(reciente.id);
    } else {
      this.abrirModalNuevaNota();
    }
  }

  agregarMedicamento() {
    console.log('Agregando medicamento...');
  }

  // CARGA: leer notas desde appointment.meta
  async loadNotasFromAppointment(): Promise<void> {
    if (!this.appointmentId) {
      this.notasMedicas = [];
      return;
    }
    try {
      const notas = await this.apptSvc.getNotasMedicas(this.appointmentId);
      this.notasMedicas = Array.isArray(notas) ? notas : [];
      // actualizar notaInterna con la nota más reciente (por created_at)
      if (this.notasMedicas.length) {
        const sorted = [...this.notasMedicas].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
        this.notaInterna = sorted[0].nota;
      } else {
        // mantener valor por defecto si no hay notas
        // this.notaInterna = '';
      }
    } catch (err) {
      console.error('Error cargando notas desde appointment.meta', err);
      this.notasMedicas = [];
    }
  }

  // ABRIR modal para nueva nota
  abrirModalNuevaNota() {
    this.modalNotaAbierta = true;
    this.editingNotaId = null;
    this.notaForm = { titulo: '', nota: '' };
  }

  // ABRIR modal para editar nota existente
  abrirModalEditarNota(notaId: string) {
    const nota = this.notasMedicas.find((n) => n.id === notaId);
    if (!nota) return;
    this.editingNotaId = notaId;
    this.notaForm = { titulo: nota.titulo, nota: nota.nota };
    this.modalNotaAbierta = true;
  }

  // GUARDAR: crea o actualiza nota en appointment.meta
  async guardarNota() {
    if (!this.appointmentId) return;
    const titulo = String(this.notaForm.titulo || '').trim();
    const notaText = String(this.notaForm.nota || '').trim();
    if (!titulo && !notaText) return;

    try {
      if (this.editingNotaId) {
        // actualizar
        await this.apptSvc.updateNotaMedica(this.appointmentId, this.editingNotaId, { titulo, nota: notaText });
      } else {
        // crear nueva
        await this.apptSvc.addNotaMedica(this.appointmentId, { titulo, nota: notaText });
      }
      // recargar local
      await this.loadNotasFromAppointment();
      // cerrar modal y limpiar formulario
      this.modalNotaAbierta = false;
      this.editingNotaId = null;
      this.notaForm = { titulo: '', nota: '' };
    } catch (err) {
      console.error('Error guardando nota en appointment.meta', err);
      throw err;
    }
  }

  // BORRAR nota
  async eliminarNota(notaId: string) {
    if (!this.appointmentId) return;
    try {
      await this.apptSvc.deleteNotaMedica(this.appointmentId, notaId);
      await this.loadNotasFromAppointment();
    } catch (err) {
      console.error('Error eliminando nota en appointment.meta', err);
      throw err;
    }
  }

  // Helper: convertir HTML -> texto plano preservando saltos de linea
  private stripHtmlToPlain(text?: string): string {
    if (!text) return '';
    // Convertir </p>, </div>, <br> a saltos de linea antes de procesar
    let processed = text
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n');
    const div = document.createElement('div');
    div.innerHTML = processed;
    // Obtener texto y limpiar saltos de linea multiples
    return (div.textContent || div.innerText || '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  // Cargar medicamentos actuales desde appointment.meta y convertir a texto plano
  async loadMedicamentosActualesFromAppointment(): Promise<void> {
    if (!this.appointmentId) {
      this.medicamentosActualesPlain = '';
      return;
    }
    try {
      const appointment: IAppointment | null = await this.apptSvc.getById(this.appointmentId);
      const html = appointment?.meta?.medicamentos_actuales?.descripcion ?? '';
      this.medicamentosActualesPlain = this.stripHtmlToPlain(html);
    } catch (err) {
      console.error('Error cargando medicamentos actuales desde appointment.meta', err);
      this.medicamentosActualesPlain = '';
    }
  }

  // Cargar medicamentos desde la última receta del paciente (indicaciones médicas)
  async loadMedicamentosDesdeRecetas(patientId: number): Promise<void> {
    if (!patientId) {
      this.medicamentosCompactos = [];
      this.medicamentosRecetasUI = [];
      this.recetasExpandidas.clear();
      return;
    }
    this.isLoadingMedicamentos = true;
    try {
      const medicamentos = await this.expedienteService.getUltimaRecetaPaciente(patientId)
        .pipe(takeUntil(this.destroy$))
        .toPromise() || [];

      const lineasCombinadas: string[] = [];
      const tarjetasRecetas: IMedicamentoRecetaCardUI[] = [];
      this.recetasExpandidas.clear();

      // Construir tarjetas en el orden original de la receta
      for (let index = 0; index < medicamentos.length; index++) {
        const item = medicamentos[index];
        const fechaReceta = item?.receta_fecha || item?.fecha_creacion || item?.created_at || undefined;

        if (item?.texto_libre) {
          const textoPlano = this.stripHtmlToPlain(item.texto_libre);
          const bloqueLimpio = textoPlano.replace(/\n{3,}/g, '\n\n').trim();
          if (!bloqueLimpio) continue;

          tarjetasRecetas.push({
            id: this.buildRecetaCardId(item, index, 'texto'),
            recetaId: item?.receta_id || item?.backendId || item?.id || undefined,
            titulo: 'Receta',
            detalles: [],
            textoLibre: bloqueLimpio,
            fechaCreacion: fechaReceta,
          });

          const lineas = bloqueLimpio.split('\n').filter((linea: string) => linea.trim() !== '');
          lineasCombinadas.push(...lineas);
          continue;
        }

        const medFormulario = this.formatMedicamentoFormularioUI(item as IMedicamentoReceta);
        tarjetasRecetas.push({
          id: this.buildRecetaCardId(item, index, 'formulario'),
          recetaId: item?.receta_id || item?.backendId || item?.id || undefined,
          titulo: medFormulario.nombre,
          detalles: medFormulario.detalles,
          indicaciones: medFormulario.indicaciones,
          fechaCreacion: fechaReceta,
        });

        const lineaFormulario = this.formatMedicamentosCompactos([item as IMedicamentoReceta]);
        lineasCombinadas.push(...lineaFormulario);
      }

      this.medicamentosRecetasUI = tarjetasRecetas;
      this.medicamentosCompactos = lineasCombinadas;
      this.medicamentosActualesPlain = lineasCombinadas.join('\n');

      console.log('[PerfilPaciente] Medicamentos cargados:', {
        tarjetas: tarjetasRecetas.length,
        total: this.medicamentosCompactos.length
      });
    } catch (err) {
      console.error('[PerfilPaciente] Error cargando medicamentos desde recetas:', err);
      this.medicamentosCompactos = [];
      this.medicamentosRecetasUI = [];
      this.recetasExpandidas.clear();
    } finally {
      this.isLoadingMedicamentos = false;
    }
  }

  // Refresca solo la parte de medicamentos actuales
  async refreshMedicamentos(): Promise<void> {
    if (!this.patientId) return;
    await this.loadMedicamentosDesdeRecetas(Number(this.patientId));
  }

  // Cargar TODOS los diagnósticos de TODAS las notas del paciente
  async loadAllDiagnosticosPaciente(patientId: number): Promise<void> {
    if (!patientId) {
      this.diagnosticosActuales = [];
      return;
    }
    this.isLoadingDiagnosticos = true;
    try {
      const diagnosticos = await this.expedienteService.getAllDiagnosticosPaciente(patientId)
        .pipe(takeUntil(this.destroy$))
        .toPromise() || [];

      console.log('[PerfilPaciente] Diagnósticos cargados de todas las notas:', diagnosticos.length);
      this.diagnosticosActuales = diagnosticos;
      this.stateService.setCurrentDiagnosticos(diagnosticos);
    } catch (err) {
      console.error('[PerfilPaciente] Error cargando diagnósticos del paciente:', err);
      this.diagnosticosActuales = [];
    } finally {
      this.isLoadingDiagnosticos = false;
    }
  }

  // Formatea fecha/hora ISO a "HH:MM a.m./p.m."
  private formatAppointmentTime(isoLike?: string): string {
    if (!isoLike) return '—';
    const d = new Date(isoLike);
    if (isNaN(d.getTime())) return '—';
    const hh = d.getHours();
    const mm = String(d.getMinutes()).padStart(2, '0');
    const h12 = ((hh + 11) % 12) + 1;
    const ampm = hh < 12 ? 'a.m.' : 'p.m.';
    return `${String(h12).padStart(2, '0')}:${mm} ${ampm}`;
  }

  // Carga resumen de la cita: hora formateada y nombre del doctor
  async loadAppointmentSummary(): Promise<void> {
    if (!this.appointmentId) {
      this.appointmentTimeDisplay = '—';
      this.appointmentDoctorName = '—';
      return;
    }
    try {
      const appt: IAppointment | null = await this.apptSvc.getById(this.appointmentId);
      // hora: preferir start_at en nivel superior
      const startAt = appt?.start_at ?? appt?.meta?.start_at ?? null;
      this.appointmentTimeDisplay = this.formatAppointmentTime(startAt);
      // doctor: usar la propiedad correcta de IAppointment
      this.appointmentDoctorName = (appt as any)?.doctorName ?? '—';
    } catch (err) {
      console.error('Error cargando resumen de la cita', err);
      this.appointmentTimeDisplay = '—';
      this.appointmentDoctorName = '—';
    }
  }

  // ==================== MÉTODOS PARA HISTÓRICO ====================

  // Carga el histórico desde la tabla patient_timelines (patient-scoped)
  async loadHistorico(): Promise<void> {
    if (!this.patientId || String(this.patientId).trim() === '') {
      console.warn('[PerfilPaciente] patientId inválido:', this.patientId);
      this.historico = [];
      this.paraclinicosDisponibles = [];
      return;
    }

    this.isLoadingHistorico = true;
    try {
      const historicoCompleto = await this.patientTimelineService.getTimeline(Number(this.patientId))
        .pipe(takeUntil(this.destroy$))
        .toPromise() || [];

      this.historico = historicoCompleto.filter(
        (categoria: IHistorico) => categoria.titulo?.toLowerCase() !== 'recetas'
      );

      try {
        const labResults = await this.labResultsService.getByPatient(Number(this.patientId));
        this.paraclinicosDisponibles = labResults.map(labResultToParaclinico);
        console.log('[PerfilPaciente] ✅ Paraclínicos cargados desde lab_results:', this.paraclinicosDisponibles.length);
      } catch (labError) {
        console.error('[PerfilPaciente] ❌ Error cargando paraclínicos desde lab_results:', labError);
        this.paraclinicosDisponibles = [];
      }

      console.log('[PerfilPaciente] ✅ Histórico cargado desde patient_timelines:', this.historico);
      console.log('[PerfilPaciente] Paraclínicos disponibles:', this.paraclinicosDisponibles);
    } catch (err) {
      console.error('[PerfilPaciente] ❌ Error cargando histórico:', err);
      this.historico = [];
      this.paraclinicosDisponibles = [];
    } finally {
      this.isLoadingHistorico = false;
    }
  }

  // Guarda el histórico en la tabla patient_timelines (patient-scoped)
  async saveHistorico(historicoData: IHistorico[]): Promise<void> {
    if (!this.patientId || String(this.patientId).trim() === '') {
      console.warn('[PerfilPaciente] patientId inválido:', this.patientId);
      this.showToast('Error: No se puede identificar al paciente.', 'danger');
      return;
    }

    try {
      console.log('[PerfilPaciente] 💾 Guardando histórico en patient_timelines:', historicoData);

      // ✅ VALIDACIÓN 1: Verificar que historicoData sea array válido
      if (!Array.isArray(historicoData) || historicoData.length === 0) {
        console.warn('[PerfilPaciente] historicoData inválido o vacío:', historicoData);
        this.showToast('No hay datos de histórico para guardar.', 'warning');
        return;
      }

      // Actualizar el histórico localmente
      this.historico = historicoData;

      // ✅ VALIDACIÓN 2: Filtrar y validar categorías antes de procesar
      const categoriasValidas = historicoData.filter(categoria => {
        // Validar que tenga título y eventos
        if (!categoria || !categoria.titulo || !categoria.titulo.trim()) {
          console.warn('[PerfilPaciente] ⚠️ Categoría sin título, omitiendo:', categoria);
          return false;
        }
        if (!Array.isArray(categoria.eventos) || categoria.eventos.length === 0) {
          console.warn('[PerfilPaciente] ⚠️ Categoría sin eventos, omitiendo:', categoria.titulo);
          return false;
        }
        return true;
      });

      if (categoriasValidas.length === 0) {
        console.warn('[PerfilPaciente] No hay categorías válidas para guardar');
        this.showToast('No hay eventos válidos para guardar en el histórico.', 'warning');
        return;
      }

      // Procesar eventos en paralelo con validación rigurosa
      const promises = categoriasValidas.flatMap(categoria =>
        categoria.eventos
          .filter(evento => {
            // ✅ VALIDACIÓN 3: Filtrar eventos inválidos (sin fecha o nombre)
            if (!evento.fecha || evento.fecha.trim() === '') {
              console.warn(`[PerfilPaciente] ⚠️ Evento sin fecha en categoría "${categoria.titulo}":`, evento);
              return false;
            }
            if (!evento.nombre || evento.nombre.trim() === '') {
              console.warn(`[PerfilPaciente] ⚠️ Evento sin nombre en categoría "${categoria.titulo}":`, evento);
              return false;
            }
            return true;
          })
          .map(async evento => {
            try {
              const eventData = {
                patient_id: Number(this.patientId),
                category: categoria.titulo.trim(),
                event_date: evento.fecha.trim(),
                event_name: evento.nombre.trim(),
                description: evento.descripcion?.trim() || '',
                medical_record_id: evento.note_id || undefined,
                lab_result_id: evento.paraclinico?.backendId || undefined,
                metadata: evento.paraclinico ? { paraclinico: evento.paraclinico } : undefined,
              };

              console.log(`[PerfilPaciente] 📤 Enviando evento al backend:`, {
                categoria: categoria.titulo,
                evento: evento.nombre,
                data: eventData
              });

              if (evento.id) {
                // Actualizar evento existente
                console.log(`[PerfilPaciente] 🔄 Actualizando evento ID ${evento.id}`);
                await this.patientTimelineService.updateEvent(evento.id, eventData)
                  .pipe(takeUntil(this.destroy$))
                  .toPromise();
              } else {
                // Crear nuevo evento
                console.log(`[PerfilPaciente] ➕ Creando nuevo evento: ${evento.nombre}`);
                const nuevoEvento = await this.patientTimelineService.createEvent(eventData)
                  .pipe(takeUntil(this.destroy$))
                  .toPromise();

                // Actualizar el ID del evento con el retornado por el backend
                if (nuevoEvento?.id) {
                  evento.id = nuevoEvento.id;
                  console.log(`[PerfilPaciente] ✅ Evento creado con ID: ${nuevoEvento.id}`);
                }
              }
            } catch (err: any) {
              console.error('[TIMELINE ERROR] ❌ Error procesando evento:', {
                eventoNombre: evento.nombre,
                categoria: categoria.titulo,
                datosEvento: evento,
                httpStatus: err.status,
                errorBody: err.error,
                validationErrors: err.error?.errors,
                errorMessage: err.error?.message || err.message,
                fullError: err
              });

              // Construir mensaje de error detallado
              let errorMsg = `Error en evento "${evento.nombre}"`;

              if (err.status) {
                errorMsg += ` (HTTP ${err.status})`;
              }

              if (err.error?.message) {
                errorMsg += `: ${err.error.message}`;
              }

              if (err.error?.errors) {
                const validationMsgs = Object.keys(err.error.errors)
                  .map(field => `${field}: ${err.error.errors[field].join(', ')}`)
                  .join('; ');
                errorMsg += ` | Validación: ${validationMsgs}`;
              }

              // Re-lanzar para que Promise.allSettled lo capture
              throw new Error(errorMsg);
            }
          })
      );

      // Ejecutar todas las promesas y capturar errores individuales
      const resultados = await Promise.allSettled(promises);

      // Analizar resultados
      const errores = resultados.filter(r => r.status === 'rejected') as PromiseRejectedResult[];
      const exitosos = resultados.filter(r => r.status === 'fulfilled');

      console.log(`[PerfilPaciente] 📊 Resultados: ${exitosos.length} exitosos, ${errores.length} fallidos`);

      if (errores.length > 0) {
        // Mostrar errores específicos
        const mensajesError = errores.map((e, i) => `${i + 1}. ${e.reason.message}`).join('\n');
        console.error('[PerfilPaciente] ❌ Errores en eventos:', mensajesError);
        this.showToast(`Se guardaron ${exitosos.length} eventos, pero ${errores.length} fallaron. Revisa que todos los campos estén completos.`, 'warning');
      } else {
        console.log('[PerfilPaciente] ✅ Histórico guardado exitosamente en patient_timelines');
        this.showToast('Histórico guardado correctamente.', 'success');
      }
    } catch (err: any) {
      console.error('[TIMELINE ERROR] ❌ Error crítico guardando histórico:', {
        fullError: err,
        httpStatus: err.status,
        errorBody: err.error,
        validationErrors: err.error?.errors,
        errorMessage: err.error?.message || err.message,
        stackTrace: err.stack
      });

      // Construir mensaje de error detallado
      let errorMessage = 'Error crítico al guardar el histórico';

      if (err.status) {
        errorMessage += ` (HTTP ${err.status})`;
      }

      errorMessage += ':\n\n';

      if (err.error?.message) {
        errorMessage += err.error.message + '\n\n';
      } else if (err.message) {
        errorMessage += err.message + '\n\n';
      }

      if (err.error?.errors) {
        errorMessage += 'Errores de validación:\n';
        Object.keys(err.error.errors).forEach(field => {
          const fieldErrors = Array.isArray(err.error.errors[field])
            ? err.error.errors[field].join(', ')
            : err.error.errors[field];
          errorMessage += `• ${field}: ${fieldErrors}\n`;
        });
        errorMessage += '\n';
      }

      errorMessage += 'Revisa la consola del navegador (F12) para más detalles técnicos.';

      this.showToast('Error crítico al guardar el histórico. Revisa la consola para más detalles.', 'danger');
    }
  }

  // ==================== MÉTODOS PARA INDICACIONES ====================

  /**
   * Carga indicaciones médicas del paciente desde múltiples fuentes:
   * 1. appointment.meta.indicaciones (si hay appointmentId)
   * 2. indicaciones_medicas/medicamentos de las notas médicas (columnas directas)
   * 3. Prescripciones del backend (tabla prescriptions) - Fuente principal
   */
  async loadIndicaciones(): Promise<void> {
    const patientId = this.patientId || this.currentPatient?.id;

    // Si no hay patientId, no podemos cargar nada
    if (!patientId) {
      console.log('[PerfilPaciente] No hay patientId para cargar indicaciones');
      this.indicacionesMedicas = [];
      this.ultimaReceta = null;
      return;
    }

    try {
      let indicacionesFromAppointment: IRecetaSolicitud[] = [];
      let indicacionesFromNotas: IRecetaSolicitud[] = [];
      let indicacionesFromBackend: IRecetaSolicitud[] = [];

      // 1. Cargar desde appointment.meta si hay appointmentId
      if (this.appointmentId) {
        const appointment: IAppointment | null = await this.apptSvc.getById(this.appointmentId);
        indicacionesFromAppointment = appointment?.meta?.indicaciones || [];
        this.plantillasRecetas = appointment?.meta?.plantillas_recetas || [];
      }

      // 2. Cargar desde las notas médicas del paciente (columnas directas)
      try {
        const notas = await this.expedienteService.getNotas(Number(patientId)).toPromise() || [];
        for (const nota of notas) {
          const indicaciones = nota.indicaciones_medicas || nota.medicamentos || [];
          if (indicaciones && Array.isArray(indicaciones)) {
            for (const ind of indicaciones) {
              if (!indicacionesFromNotas.find(i => i.id === ind.id)) {
                indicacionesFromNotas.push(ind);
              }
            }
          }
        }
      } catch (err) {
        console.warn('[PerfilPaciente] Error cargando indicaciones desde notas:', err);
      }

      // 3. Cargar prescripciones del backend y convertir al formato de indicaciones
      try {
        const prescriptions = await this.prescriptionService.getByPatient(Number(patientId));
        indicacionesFromBackend = this.prescriptionService.convertToFrontendFormat(prescriptions);
      } catch (err) {
        console.warn('[PerfilPaciente] Error cargando prescripciones del backend:', err);
      }

      // Combinar todas las fuentes, priorizando las más recientes
      const allIndicaciones: IRecetaSolicitud[] = [];
      const seenIds = new Set<string>();

      // Primero las del appointment (son las de la sesión actual)
      for (const ind of indicacionesFromAppointment) {
        if (!seenIds.has(ind.id)) {
          seenIds.add(ind.id);
          allIndicaciones.push(ind);
        }
      }

      // Luego las de las notas (columnas directas)
      for (const ind of indicacionesFromNotas) {
        if (!seenIds.has(ind.id)) {
          seenIds.add(ind.id);
          allIndicaciones.push(ind);
        }
      }

      // Finalmente las del backend (tabla prescriptions) - Fuente principal normalizada
      for (const ind of indicacionesFromBackend) {
        if (!seenIds.has(ind.id)) {
          seenIds.add(ind.id);
          allIndicaciones.push(ind);
        }
      }

      // Ordenar por fecha de creación (más reciente primero)
      allIndicaciones.sort((a, b) => {
        const dateA = new Date(a.fecha_creacion).getTime();
        const dateB = new Date(b.fecha_creacion).getTime();
        return dateB - dateA;
      });

      this.indicacionesMedicas = allIndicaciones;

      // Obtener la última receta para referencia (pero NO establecer medicamentosCompactos aquí)
      // medicamentosCompactos se establece únicamente en loadMedicamentosDesdeRecetas para evitar race conditions
      if (this.indicacionesMedicas.length > 0) {
        const recetas = this.indicacionesMedicas.filter((ind) => ind.tipo === 'receta');
        if (recetas.length > 0) {
          this.ultimaReceta = recetas[0]; // La primera es la más reciente
        }
      }

      console.log('[PerfilPaciente] Indicaciones cargadas (total):', this.indicacionesMedicas.length);
      console.log('[PerfilPaciente] - Desde appointment:', indicacionesFromAppointment.length);
      console.log('[PerfilPaciente] - Desde notas:', indicacionesFromNotas.length);
      console.log('[PerfilPaciente] - Desde backend:', indicacionesFromBackend.length);
    } catch (error) {
      console.error('[PerfilPaciente] Error cargando indicaciones:', error);
      this.indicacionesMedicas = [];
      this.ultimaReceta = null;
    }
  }

  // Formatea medicamentos en versión ultra compacta
  formatMedicamentosCompactos(medicamentos: IMedicamentoReceta[]): string[] {
    return medicamentos.map((med) => {
      const nombre = med.nombre || 'Sin nombre';
      const partes: string[] = [nombre];

      // Agregar dosis si existe y no está ya incluida en el nombre
      if (med.dosis && !nombre.toLowerCase().includes(med.dosis.toLowerCase())) {
        partes.push(med.dosis);
      }

      // Agregar vía si existe
      if (med.via) {
        partes.push(`(${med.via})`);
      }

      // Formatear frecuencia de forma compacta
      if (med.frecuencia) {
        const frecCompacta = this.formatFrecuenciaCompacta(med.frecuencia);
        partes.push(frecCompacta);
      }

      return partes.join(' ');
    });
  }

  private formatMedicamentoFormularioUI(med: IMedicamentoReceta): IMedicamentoFormularioUI {
    const nombre = (med.nombre || 'Sin nombre').trim();
    const detalles: string[] = [];

    if (med.dosis?.trim()) detalles.push(`Dosis: ${med.dosis.trim()}`);
    if (med.via?.trim()) detalles.push(`Vía: ${med.via.trim()}`);
    if (med.frecuencia?.trim()) detalles.push(`Frecuencia: ${this.formatFrecuenciaCompacta(med.frecuencia)}`);
    if (med.duracion?.trim()) detalles.push(`Duración: ${med.duracion.trim()}`);

    const indicaciones = med.indicaciones
      ? this.stripHtmlToPlain(med.indicaciones).trim()
      : '';

    return {
      nombre,
      detalles,
      indicaciones: indicaciones || undefined,
    };
  }

  private formatFrecuenciaCompacta(frecuencia: string): string {
    if (!frecuencia) return '';
    const frec = frecuencia.toLowerCase().trim();

    // Mapear frecuencias comunes a formato compacto
    if (frec.includes('cada 24') || frec.includes('c/24') || frec === '24 horas') return 'c/24h';
    if (frec.includes('cada 12') || frec.includes('c/12') || frec === '12 horas') return 'c/12h';
    if (frec.includes('cada 8') || frec.includes('c/8') || frec === '8 horas') return 'c/8h';
    if (frec.includes('cada 6') || frec.includes('c/6') || frec === '6 horas') return 'c/6h';
    if (frec.includes('cada 4') || frec.includes('c/4') || frec === '4 horas') return 'c/4h';
    if (frec.includes('prn') || frec.includes('sos') || frec.includes('necesario')) return 'PRN';
    if (frec.includes('diari') || frec.includes('dia') || frec === '1 vez') return '1x/día';

    // Si ya es formato compacto, devolverlo
    if (frec.length <= 8) return frecuencia;

    // Extraer número de horas si es posible
    const match = frec.match(/(\d+)\s*h/i);
    if (match) return `c/${match[1]}h`;

    return frecuencia;
  }

  // Abre modal de indicaciones completas
  async abrirModalIndicaciones(selectedRecetaId?: string | number): Promise<void> {
    this.modalSelectedRecetaId = selectedRecetaId ?? null;
    // Cargar indicaciones antes de abrir el modal
    await this.loadIndicaciones();
    this.modalIndicacionesAbierto = true;
  }

  // Cierra modal de indicaciones
  cerrarModalIndicaciones(): void {
    this.modalIndicacionesAbierto = false;
    this.modalSelectedRecetaId = null;
  }

  async abrirModalResumenCompleto(): Promise<void> {
    if (!this.notaSeleccionada) return;

    this.modalResumenCompletoAbierto = true;
    this.isLoadingResumenCompleto = true;
    this.errorResumenCompleto = null;
    this.notasResumenCompleto = [];

    const patientId = this.getCurrentPatientIdAsNumber();

    try {
      const notasBase = patientId
        ? (await this.expedienteService.getNotas(patientId).pipe(takeUntil(this.destroy$)).toPromise()) || []
        : [];

      const idsNotas = new Set<number>();
      for (const nota of notasBase) {
        const notaId = this.normalizarNotaId(nota?.id);
        if (notaId) idsNotas.add(notaId);
      }

      const notaSeleccionadaId = this.normalizarNotaId(this.notaSeleccionada?.id);
      if (notaSeleccionadaId) {
        idsNotas.add(notaSeleccionadaId);
      }

      const notasDetalladas = await Promise.all(
        Array.from(idsNotas).map(async (notaId) => {
          try {
            const notaCompleta = await this.expedienteService
              .getNota(notaId)
              .pipe(takeUntil(this.destroy$))
              .toPromise();

            return notaCompleta ?? notasBase.find((nota) => this.normalizarNotaId(nota?.id) === notaId) ?? null;
          } catch {
            return notasBase.find((nota) => this.normalizarNotaId(nota?.id) === notaId) ?? null;
          }
        })
      );

      const notasFallback = this.notaSeleccionada ? [this.notaSeleccionada] : [];
      const notasFinales = notasDetalladas.length > 0 ? notasDetalladas : notasFallback;

      this.notasResumenCompleto = this.ordenarNotasResumenCompleto(notasFinales.filter((nota) => !!nota));
      this.errorResumenCompleto = null;
      this.actualizarContextoNotaActualResumenCompleto();
    } catch (error) {
      console.error('[PerfilPaciente] Error cargando notas para resumen completo:', error);
      this.notasResumenCompleto = this.notaSeleccionada ? [this.notaSeleccionada] : [];
      this.errorResumenCompleto = this.notasResumenCompleto.length
        ? null
        : 'No se pudieron cargar las notas medicas del paciente.';
      this.actualizarContextoNotaActualResumenCompleto();
    } finally {
      this.isLoadingResumenCompleto = false;
    }
  }

  cerrarModalResumenCompleto(): void {
    this.modalResumenCompletoAbierto = false;
    this.isLoadingResumenCompleto = false;
    this.errorResumenCompleto = null;
    this.indiceNotaActualResumenCompleto = -1;
  }

  trackByNotaResumenCompleto(index: number, nota: any): string {
    const parsedId = Number(nota?.id);
    if (Number.isFinite(parsedId) && parsedId > 0) {
      return `nota-${Math.trunc(parsedId)}`;
    }

    const rawDate = nota?.created_at || nota?.fecha || nota?.updated_at;
    const timestamp = rawDate ? new Date(rawDate).getTime() : 0;
    const safeTimestamp = Number.isFinite(timestamp) ? timestamp : 0;
    return `nota-${index}-${safeTimestamp}`;
  }

  private normalizarNotaId(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.trunc(parsed);
  }

  private getNotaTimestamp(nota: any): number {
    const rawDate = nota?.created_at || nota?.fecha || nota?.updated_at;
    if (!rawDate) return 0;
    const time = new Date(rawDate).getTime();
    return Number.isFinite(time) ? time : 0;
  }

  private ordenarNotasResumenCompleto(notas: any[]): any[] {
    const notasUnicas = new Map<string, any>();

    for (const nota of notas) {
      const notaId = this.normalizarNotaId(nota?.id);
      const key = notaId ? `id-${notaId}` : `fallback-${this.getNotaTimestamp(nota)}-${nota?.type || 'nota'}`;

      if (!notasUnicas.has(key)) {
        notasUnicas.set(key, nota);
      }
    }

    return Array.from(notasUnicas.values()).sort(
      (a, b) => this.getNotaTimestamp(b) - this.getNotaTimestamp(a)
    );
  }

  esNotaActualEnResumenCompleto(nota: any): boolean {
    const notaActualId = this.normalizarNotaId(this.notaSeleccionada?.id);
    const notaId = this.normalizarNotaId(nota?.id);
    if (!notaActualId || !notaId) return false;
    return notaActualId === notaId;
  }

  get tieneNotasArribaDeNotaActual(): boolean {
    return this.indiceNotaActualResumenCompleto > 0;
  }

  get tieneNotasAbajoDeNotaActual(): boolean {
    return this.indiceNotaActualResumenCompleto >= 0
      && this.indiceNotaActualResumenCompleto < (this.notasResumenCompleto.length - 1);
  }

  private actualizarContextoNotaActualResumenCompleto(): void {
    const notaActualId = this.normalizarNotaId(this.notaSeleccionada?.id);
    if (!notaActualId || !this.notasResumenCompleto.length) {
      this.indiceNotaActualResumenCompleto = -1;
      return;
    }

    this.indiceNotaActualResumenCompleto = this.notasResumenCompleto.findIndex(
      (nota) => this.normalizarNotaId(nota?.id) === notaActualId
    );

    if (this.indiceNotaActualResumenCompleto >= 0) {
      this.desplazarAModalNotaActual(notaActualId);
    }
  }

  private desplazarAModalNotaActual(notaId: number): void {
    setTimeout(() => {
      if (!this.modalResumenCompletoAbierto) return;

      const cards = this.notaCardsResumenCompleto?.toArray() || [];
      const cardTarget = cards.find((card) => {
        const attr = card.nativeElement.getAttribute('data-nota-id');
        return this.normalizarNotaId(attr) === notaId;
      });

      cardTarget?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  // Abre modal para ver paraclínico (desde histórico)
  abrirModalVerParaclinico(paraclinico: any): void {
    console.log('[PerfilPaciente] abrirModalVerParaclinico - recibido:', paraclinico);
    console.log('[PerfilPaciente] paraclinicosDisponibles:', this.paraclinicosDisponibles);

    // Buscar el paraclinico completo en la lista de disponibles
    let paraclinicoCompleto: IParaclinico | undefined;

    // 1. Buscar por lab_result_id (viene del histórico)
    if (paraclinico.lab_result_id) {
      paraclinicoCompleto = this.paraclinicosDisponibles.find(
        (p) => p.backendId === paraclinico.lab_result_id
      );
      console.log('[PerfilPaciente] Búsqueda por lab_result_id:', paraclinico.lab_result_id, '-> encontrado:', !!paraclinicoCompleto);
    }

    // 2. Buscar por backendId (número)
    if (!paraclinicoCompleto && paraclinico.backendId) {
      paraclinicoCompleto = this.paraclinicosDisponibles.find(
        (p) => p.backendId === paraclinico.backendId
      );
      console.log('[PerfilPaciente] Búsqueda por backendId:', paraclinico.backendId, '-> encontrado:', !!paraclinicoCompleto);
    }

    // 3. Buscar por id (string)
    if (!paraclinicoCompleto && paraclinico.id) {
      paraclinicoCompleto = this.paraclinicosDisponibles.find(
        (p) => p.id === paraclinico.id || p.backendId?.toString() === paraclinico.id
      );
      console.log('[PerfilPaciente] Búsqueda por id:', paraclinico.id, '-> encontrado:', !!paraclinicoCompleto);
    }

    // 4. Buscar por nombre como último recurso
    if (!paraclinicoCompleto && paraclinico.nombre) {
      paraclinicoCompleto = this.paraclinicosDisponibles.find(
        (p) => p.nombre === paraclinico.nombre
      );
      console.log('[PerfilPaciente] Búsqueda por nombre:', paraclinico.nombre, '-> encontrado:', !!paraclinicoCompleto);
    }

    // Usar el paraclinico completo si se encontró, sino usar el básico
    const paraclinicoFinal = paraclinicoCompleto || paraclinico;

    console.log('[PerfilPaciente] Paraclínico final:', {
      usandoCompleto: !!paraclinicoCompleto,
      tieneInterpretacion: !!paraclinicoFinal.interpretacion,
      tieneFecha: !!paraclinicoFinal.fecha,
      tienePdfUrl: !!paraclinicoFinal.pdfUrl,
      paraclinicoFinal
    });

    this.paraclinicoParaVer = {
      ...paraclinicoFinal,
      pdfUrlSafe: paraclinicoFinal.pdfUrl
        ? this.sanitizer.bypassSecurityTrustResourceUrl(paraclinicoFinal.pdfUrl)
        : undefined,
    };
    this.modalVerParaclinicoAbierto = true;
  }

  // Cierra modal de ver paraclínico
  cerrarModalVerParaclinico(): void {
    this.modalVerParaclinicoAbierto = false;
    this.paraclinicoParaVer = null;
  }

  // Verifica si el paraclínico a ver es una imagen
  esImagenParaclinicoVer(): boolean {
    if (!this.paraclinicoParaVer?.pdfUrl) return false;
    const url = this.paraclinicoParaVer.pdfUrl.toLowerCase();
    return url.includes('.png') || url.includes('.jpg') || url.includes('.jpeg');
  }

  // Compartir paraclínico por WhatsApp
  compartirParaclinicoPorWhatsapp(pdfUrl: string | undefined): void {
    if (!pdfUrl) return;
    const url = `https://wa.me/?text=${encodeURIComponent('Te comparto el estudio: ' + pdfUrl)}`;
    window.open(url, '_blank');
  }

  /**
   * Guarda indicaciones actualizadas
   * Soporta tanto medicamentos estructurados como texto libre (contenido_html)
   */
  async saveIndicaciones(indicaciones: IRecetaSolicitud[]): Promise<void> {
    const patientId = this.patientId || this.currentPatient?.id;
    if (!patientId) {
      console.warn('[PerfilPaciente] No hay patientId para guardar las indicaciones');
      return;
    }

    try {
      const recetasNuevas = indicaciones.filter((ind) => {
        if (ind.tipo !== 'receta' || ind.backendId) return false;

        const esModoTexto = ind.modo_entrada === 'texto';
        const tieneContenidoTexto = esModoTexto && ind.contenido_html?.trim();
        const tieneMedicamentos = ind.medicamentos && ind.medicamentos.length > 0;

        return tieneContenidoTexto || tieneMedicamentos;
      });

      console.log('[PerfilPaciente] Guardando indicaciones. Recetas nuevas:', recetasNuevas.length);

      for (const receta of recetasNuevas) {
        try {
          const prescriptionCreada = await this.prescriptionService.create(
            patientId,
            receta.medicamentos || [],
            receta.notas,
            this.appointmentId,
            {
              modoEntrada: receta.modo_entrada,
              contenidoHtml: receta.contenido_html
            }
          );
          console.log('[PerfilPaciente] Receta guardada en backend:', prescriptionCreada.id);

          receta.backendId = prescriptionCreada.id;
          receta.finalizada = true;
        } catch (error) {
          console.error('[PerfilPaciente] Error guardando receta individual:', error);
        }
      }

      this.indicacionesMedicas = indicaciones;

      await this.loadMedicamentosDesdeRecetas(Number(patientId));

      console.log('[PerfilPaciente] Indicaciones guardadas correctamente');
    } catch (error) {
      console.error('[PerfilPaciente] Error guardando indicaciones:', error);
    }
  }

  // Guarda nueva plantilla de receta
  async savePlantillaReceta(plantilla: IPlantillaReceta): Promise<void> {
    if (!this.appointmentId) return;

    try {
      const appointment: IAppointment | null = await this.apptSvc.getById(this.appointmentId);
      const plantillas = appointment?.meta?.plantillas_recetas || [];
      plantillas.push(plantilla);

      const updatedMeta = {
        ...appointment?.meta,
        plantillas_recetas: plantillas,
      };

      await this.apptSvc.update(this.appointmentId, { meta: updatedMeta });
      await this.loadIndicaciones();
      console.log('[PerfilPaciente] Plantilla guardada correctamente');
    } catch (error) {
      console.error('[PerfilPaciente] Error guardando plantilla:', error);
    }
  }

  // Actualiza plantilla existente
  async updatePlantillaReceta(plantilla: IPlantillaReceta): Promise<void> {
    if (!this.appointmentId) return;

    try {
      const appointment: IAppointment | null = await this.apptSvc.getById(this.appointmentId);
      let plantillas = appointment?.meta?.plantillas_recetas || [];
      const index = plantillas.findIndex((p: IPlantillaReceta) => p.id === plantilla.id);

      if (index !== -1) {
        plantillas[index] = plantilla;
      }

      const updatedMeta = {
        ...appointment?.meta,
        plantillas_recetas: plantillas,
      };

      await this.apptSvc.update(this.appointmentId, { meta: updatedMeta });
      await this.loadIndicaciones();
      console.log('[PerfilPaciente] Plantilla actualizada correctamente');
    } catch (error) {
      console.error('[PerfilPaciente] Error actualizando plantilla:', error);
    }
  }

  // Elimina plantilla
  async deletePlantillaReceta(plantillaId: string): Promise<void> {
    if (!this.appointmentId) return;

    try {
      const appointment: IAppointment | null = await this.apptSvc.getById(this.appointmentId);
      let plantillas = appointment?.meta?.plantillas_recetas || [];
      plantillas = plantillas.filter((p: IPlantillaReceta) => p.id !== plantillaId);

      const updatedMeta = {
        ...appointment?.meta,
        plantillas_recetas: plantillas,
      };

      await this.apptSvc.update(this.appointmentId, { meta: updatedMeta });
      await this.loadIndicaciones();
      console.log('[PerfilPaciente] Plantilla eliminada correctamente');
    } catch (error) {
      console.error('[PerfilPaciente] Error eliminando plantilla:', error);
    }
  }

  // ==================== RECETAS HISTORICAS ====================

  /**
   * Carga todas las recetas historicas del paciente desde el backend
   * Las recetas se muestran en "Medicamentos actuales", NO en el historico
   */
  async loadRecetasHistoricas(): Promise<void> {
    if (!this.patientId || String(this.patientId).trim() === '') {
      console.warn('[PerfilPaciente] patientId invalido para cargar recetas');
      return;
    }

    try {
      console.log('[PerfilPaciente] Cargando recetas historicas del paciente:', this.patientId);
      this.recetasHistoricas = await this.prescriptionService.getByPatient(Number(this.patientId));
      console.log('[PerfilPaciente] Recetas historicas cargadas:', this.recetasHistoricas.length);
    } catch (error) {
      console.error('[PerfilPaciente] Error cargando recetas historicas:', error);
      this.recetasHistoricas = [];
    }
  }

  /**
   * Convierte las recetas del backend al formato IHistorico
   * y las agrega como una categoria en el historico
   */
  private agregarRecetasAlHistorico(): void {
    if (!this.recetasHistoricas || this.recetasHistoricas.length === 0) {
      console.log('[PerfilPaciente] No hay recetas para agregar al historico');
      return;
    }

    // Remover categoria "Recetas" existente si la hay
    const indexRecetas = this.historico.findIndex(h => h.titulo === 'Recetas');
    if (indexRecetas !== -1) {
      this.historico.splice(indexRecetas, 1);
    }

    // Convertir recetas a eventos del historico
    // NOTA: No se asigna 'id' porque receta.id es de la tabla prescriptions,
    // no de patient_timelines. Esto evita errores 404 al intentar actualizar.
    const eventosRecetas = this.recetasHistoricas.map(receta => {
      const fecha = new Date(receta.created_at);
      const fechaFormateada = fecha.toISOString().split('T')[0];
      const nombreReceta = `R-${fecha.getDate()}/${fecha.getMonth() + 1}/${fecha.getFullYear()}`;

      // Generar descripcion con los medicamentos
      const medicamentos = receta.items?.map(item =>
        `${item.medication}${item.dosage ? ' ' + item.dosage : ''}${item.frequency ? ' - ' + item.frequency : ''}`
      ).join(', ') || 'Sin medicamentos';

      return {
        // id se omite - receta.id es de prescriptions, no de patient_timelines
        prescription_id: receta.id, // Referencia a la receta original
        fecha: fechaFormateada,
        nombre: nombreReceta,
        descripcion: medicamentos,
      };
    });

    // Agregar la categoria "Recetas" al historico
    if (eventosRecetas.length > 0) {
      const categoriaRecetas: IHistorico = {
        titulo: 'Recetas',
        eventos: eventosRecetas,
      };
      this.historico.push(categoriaRecetas);
      console.log('[PerfilPaciente] Categoria Recetas agregada al historico con', eventosRecetas.length, 'eventos');
    }
  }

  // ==================== NOTAS INTERNAS DEL PACIENTE ====================

  async loadNotasInternas(): Promise<void> {
    if (!this.patientId || String(this.patientId).trim() === '') {
      console.warn('[PerfilPaciente] patientId inválido:', this.patientId);
      return;
    }

    try {
      this.notasInternas = await this.patientInternalNotesService.getByPatient(Number(this.patientId))
        .pipe(takeUntil(this.destroy$))
        .toPromise() || [];
      console.log('[PerfilPaciente] Notas internas cargadas desde BD:', this.notasInternas.length);
      this.calcularNotasNuevas();
    } catch (error) {
      console.error('[PerfilPaciente] Error cargando notas internas:', error);
      this.notasInternas = [];
      this.notasNuevasCount = 0;
    }
  }

  private calcularNotasNuevas(): void {
    const storageKey = `notas_vistas_paciente_${this.patientId}`;
    const ultimaVistaStr = localStorage.getItem(storageKey);

    if (!ultimaVistaStr) {
      this.notasNuevasCount = this.notasInternas.length;
      return;
    }

    const ultimaVista = new Date(ultimaVistaStr).getTime();
    this.notasNuevasCount = this.notasInternas.filter(nota => {
      const fechaNota = nota.created_at ? new Date(nota.created_at).getTime() : 0;
      return fechaNota > ultimaVista;
    }).length;
  }

  private marcarNotasComoVistas(): void {
    if (!this.patientId) return;
    const storageKey = `notas_vistas_paciente_${this.patientId}`;
    localStorage.setItem(storageKey, new Date().toISOString());
    this.notasNuevasCount = 0;
  }

  /**
   * Abrir modal de notas internas del paciente
   */
  openNotasModal(): void {
    console.log('[PerfilPaciente] Abriendo modal de notas internas');
    this.showNotasModal = true;
    this.marcarNotasComoVistas();
  }

  /**
   * Cerrar modal de notas internas del paciente
   */
  closeNotasModal(): void {
    console.log('[PerfilPaciente] Cerrando modal de notas internas');
    this.showNotasModal = false;
  }

  async handleGuardarNota(notaData: { titulo: string; contenido: string }): Promise<void> {
    if (!this.patientId) {
      console.warn('[PerfilPaciente] No hay patientId para guardar nota interna');
      return;
    }

    try {
      const nuevaNota = await this.patientInternalNotesService.create({
        patient_id: Number(this.patientId),
        titulo: notaData.titulo,
        contenido: notaData.contenido,
      }).pipe(takeUntil(this.destroy$)).toPromise();

      console.log('[PerfilPaciente] Nota interna guardada:', nuevaNota);

      await this.loadNotasInternas();
    } catch (error) {
      console.error('[PerfilPaciente] Error guardando nota interna:', error);
      this.showToast('Error al guardar la nota. Por favor intenta de nuevo.', 'danger');
    }
  }

  // ==================== TAB TOGGLE: PERFIL vs RESUMEN ====================

  /**
   * Cambiar entre vista de perfil y vista de resumen de nota
   */
  cambiarVista(vista: 'perfil' | 'resumen'): void {
    this.vistaActual = vista;
    if (vista === 'perfil') {
      this.notaSeleccionada = null;
      this.errorCargaNota = null;
      this.isLoadingNota = false;
      this.modalResumenCompletoAbierto = false;
      this.notasResumenCompleto = [];
      this.isLoadingResumenCompleto = false;
      this.errorResumenCompleto = null;
      this.indiceNotaActualResumenCompleto = -1;
    }
  }

  /**
   * Obtener patientId validado como número o null
   * Evita que el binding [patientId] genere NaN
   */
  getValidPatientId(): number | null {
    if (!this.patientId) return null;
    const id = Number(this.patientId);
    return isNaN(id) ? null : id;
  }
  /**
   * Mapea un evento del histórico a la estructura de nota esperada por resumen-nota
   * @param evento - Evento histórico
   * @param notaId - ID de la nota (opcional)
   * @returns Objeto nota mapeado
   */
  private mapEventoToNota(evento: any, notaId?: string): any {
    return {
      id: notaId,
      nombre: evento.nombre || evento.type || 'Sin nombre',
      fecha: evento.created_at || evento.fecha || new Date().toISOString(),
      descripcion: evento.descripcion || evento.diagnosis || evento.resumen || '',
      type: evento.type,
      status: evento.status,
      ...(evento.soap_subjetivo && { soap_subjetivo: evento.soap_subjetivo }),
      ...(evento.soap_objetivo && { soap_objetivo: evento.soap_objetivo }),
      ...(evento.soap_analisis && { soap_analisis: evento.soap_analisis }),
      ...(evento.soap_plan && { soap_plan: evento.soap_plan }),
    };
  }

  /**
   * Muestra una nota en el sidebar de resumen sin hacer llamada adicional al backend.
   * Se usa cuando el contenedor ya tiene la nota seleccionada y queremos evitar doble fetch.
   */
  mostrarResumenNotaDirecta(nota: any): void {
    if (!nota) return;

    this.errorCargaNota = null;
    this.notaSeleccionada = nota;
    this.modalResumenCompletoAbierto = false;
    this.notasResumenCompleto = [];
    this.isLoadingResumenCompleto = false;
    this.errorResumenCompleto = null;
    this.indiceNotaActualResumenCompleto = -1;
    this.isLoadingNota = false;
    this.vistaActual = 'resumen';
  }

  /**
   * Manejar selección de nota desde el componente histórico
   * @param evento - Evento histórico que puede contener un note_id para obtener la nota completa
   */
  async onNotaSeleccionada(evento: any): Promise<void> {
    // Validar que el evento no sea null o undefined
    if (!evento) {
      console.warn('[PerfilPaciente] Evento inválido o vacío');
      return;
    }

    console.log('[PerfilPaciente] Evento seleccionado para resumen:', evento);

    // Resetear estado
    this.errorCargaNota = null;
    this.notaSeleccionada = null;
    this.modalResumenCompletoAbierto = false;
    this.notasResumenCompleto = [];
    this.isLoadingResumenCompleto = false;
    this.errorResumenCompleto = null;
    this.indiceNotaActualResumenCompleto = -1;

    // Detectar si tiene id o note_id (compatibilidad con ambos formatos)
    // Usar nullish coalescing (??) en lugar de OR (||) para mejor manejo de valores falsy
    const notaId = evento.id ?? evento.note_id;

    if (notaId) {
      console.log('[PerfilPaciente] Obteniendo nota completa del backend. ID:', notaId);
      this.isLoadingNota = true;
      this.vistaActual = 'resumen';

      try {
        // Obtener nota completa del backend
        const notaCompleta = await this.expedienteService.getNota(notaId)
          .pipe(takeUntil(this.destroy$))
          .toPromise();
        console.log('[PerfilPaciente] Nota completa obtenida:', notaCompleta);
        this.notaSeleccionada = notaCompleta;
        this.errorCargaNota = null;
      } catch (error) {
        console.error('[PerfilPaciente] Error al obtener nota completa:', error);
        this.errorCargaNota = 'No se pudo cargar la nota médica. Por favor intenta de nuevo.';

        // Usar método centralizado para mapear evento a nota
        this.notaSeleccionada = this.mapEventoToNota(evento, notaId);
      } finally {
        this.isLoadingNota = false;
      }
    } else {
      // Si no tiene ID, usar método centralizado para mapear datos básicos
      console.log('[PerfilPaciente] Evento sin ID, usando datos básicos');
      this.notaSeleccionada = this.mapEventoToNota(evento);
      this.vistaActual = 'resumen';
    }
  }

  abrirModalHistoricoPdf(): void {
    this.modalHistoricoPdfAbierto = true;
    this.historicoPdfVista = 'lista';
    this.historicoPdfSeleccionado = null;
    this.errorHistoricoPdf = null;
    this.archivoHistoricoPdf = null;
    this.cargarHistoricoPdfs();
  }

  cerrarModalHistoricoPdf(): void {
    this.modalHistoricoPdfAbierto = false;
    this.historicoPdfVista = 'lista';
    this.historicoPdfSeleccionado = null;
    this.errorHistoricoPdf = null;
    this.archivoHistoricoPdf = null;
  }

  async cargarHistoricoPdfs(): Promise<void> {
    const patientId = this.getCurrentPatientIdAsNumber();
    if (!patientId) {
      this.historicoPdfs = [];
      return;
    }

    this.isLoadingHistoricoPdfs = true;
    this.errorHistoricoPdf = null;

    try {
      this.historicoPdfs = await this.patientHistoricoPdfService.getByPatient(patientId);
    } catch (error) {
      console.error('[PerfilPaciente] Error cargando historico PDFs:', error);
      this.historicoPdfs = [];
      this.errorHistoricoPdf = 'No se pudieron cargar los PDFs.';
    } finally {
      this.isLoadingHistoricoPdfs = false;
    }
  }

  async onArchivoHistoricoPdfChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    this.errorHistoricoPdf = null;

    if (!file) {
      this.archivoHistoricoPdf = null;
      return;
    }

    const esPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!esPdf) {
      this.archivoHistoricoPdf = null;
      this.errorHistoricoPdf = 'Solo se permiten archivos PDF.';
      input.value = '';
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.archivoHistoricoPdf = null;
      this.errorHistoricoPdf = 'El archivo es muy grande. Maximo 10 MB.';
      input.value = '';
      return;
    }

    this.archivoHistoricoPdf = file;
    await this.subirHistoricoPdf();
    input.value = '';
  }

  limpiarArchivoHistoricoPdf(): void {
    this.archivoHistoricoPdf = null;
    this.errorHistoricoPdf = null;
  }

  async subirHistoricoPdf(): Promise<void> {
    if (this.subiendoHistoricoPdf) return;

    const patientId = this.getCurrentPatientIdAsNumber();
    if (!patientId) {
      this.errorHistoricoPdf = 'No se encontro el paciente.';
      return;
    }

    if (!this.archivoHistoricoPdf) {
      this.errorHistoricoPdf = 'Selecciona un archivo PDF.';
      return;
    }

    this.subiendoHistoricoPdf = true;
    this.errorHistoricoPdf = null;

    try {
      const nuevoPdf = await this.patientHistoricoPdfService.upload(patientId, this.archivoHistoricoPdf);
      this.historicoPdfs = [nuevoPdf, ...this.historicoPdfs];
      this.archivoHistoricoPdf = null;
      await this.showToast('PDF guardado correctamente.', 'success');
    } catch (error) {
      console.error('[PerfilPaciente] Error subiendo historico PDF:', error);
      this.errorHistoricoPdf = 'No se pudo subir el PDF.';
      await this.showToast('Error al subir el PDF.', 'danger');
    } finally {
      this.subiendoHistoricoPdf = false;
    }
  }

  async eliminarHistoricoPdf(pdf: IHistoricoPdf): Promise<void> {
    try {
      await this.patientHistoricoPdfService.delete(pdf.id);
      this.historicoPdfs = this.historicoPdfs.filter((item) => item.id !== pdf.id);
      await this.showToast('PDF eliminado.', 'success');
    } catch (error) {
      console.error('[PerfilPaciente] Error eliminando PDF:', error);
      await this.showToast('No se pudo eliminar el PDF.', 'danger');
    }
  }

  abrirPdfHistorico(pdf: IHistoricoPdf): void {
    if (!pdf?.file_url) return;
    this.historicoPdfSeleccionado = {
      ...pdf,
      file_url_safe: this.sanitizer.bypassSecurityTrustResourceUrl(pdf.file_url),
    };
    this.historicoPdfVista = 'visor';
  }

  volverAListaHistoricoPdf(): void {
    this.historicoPdfVista = 'lista';
    this.historicoPdfSeleccionado = null;
  }

  esImagenHistoricoPdfSeleccionado(): boolean {
    const url = (this.historicoPdfSeleccionado?.file_url || '').toLowerCase();
    const mime = (this.historicoPdfSeleccionado?.mime_type || '').toLowerCase();
    return mime.startsWith('image/') || url.endsWith('.png') || url.endsWith('.jpg') || url.endsWith('.jpeg');
  }

  formatFileSize(size?: number | null): string {
    if (!size || size <= 0) return 'N/A';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = size;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }

    const decimals = value >= 10 || unitIndex === 0 ? 0 : 1;
    return `${value.toFixed(decimals)} ${units[unitIndex]}`;
  }

  irAHistorico(): void {
    this.historicoSeccion?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}
